// Programmatic SEO entries — feeds three dynamic templates on the site :
//   /frances/como-dizer/:slug     "How to say X in French" (top BR search intent)
//   /frances/pronuncia/:slug      "How to pronounce X in French" (voice + IPA)
//   /frances/diferenca/:slug      "Difference between X and Y in French"
//
// Each entry is trilingual, ships its own TL;DR + curated FAQ (fed straight
// into FAQPage JSON-LD + Speakable schema for Google AI Overview extraction).
//
// Audio: `audioUrl` is optional. If present, renders TeacherVoicePlayer;
// otherwise the player is hidden gracefully. MP3s can be generated in a batch
// via /app/backend/scripts/generate_all_audios.py (uses the cloned voice
// H48rfufxteAbKaF2X8Ep).

// ------------------------------------------------------------------
// "Como dizer X em francês" — 10 entries
// ------------------------------------------------------------------
export const HOW_TO_SAY = [
  {
    slug: "obrigado",
    updated: "2026-02-24",
    pt: "obrigado",
    en: "thank you",
    fr: "merci",
    ipa: "/mɛʁ.si/",
    variations: [
      { fr: "Merci", pt: "Obrigado (neutro, sempre funciona)", en: "Thank you (neutral, always works)", ipa: "/mɛʁ.si/" },
      { fr: "Merci beaucoup", pt: "Muito obrigado", en: "Thank you very much", ipa: "/mɛʁ.si bo.ku/" },
      { fr: "Merci bien", pt: "Obrigadinho (casual)", en: "Thanks (casual)", ipa: "/mɛʁ.si bjɛ̃/" },
      { fr: "Merci mille fois", pt: "Mil vezes obrigado (efusivo)", en: "A thousand thanks (effusive)", ipa: "/mɛʁ.si mil fwa/" },
      { fr: "Je vous remercie", pt: "Eu vos agradeço (formal)", en: "I thank you (formal)", ipa: "/ʒə vu ʁə.mɛʁ.si/" },
    ],
    faq: {
      q1: {
        pt: { q: "Merci ou merci beaucoup?", a: "« Merci » funciona em 90% dos casos. Use « merci beaucoup » quando quiser enfatizar (favor grande, elogio recebido). « Je vous remercie » só em contexto muito formal (email de trabalho, autoridade)." },
        en: { q: "Merci or merci beaucoup?", a: "« Merci » works 90 % of the time. Use « merci beaucoup » to emphasise (big favour, compliment received). « Je vous remercie » only in very formal contexts (work email, authority)." },
        fr: { q: "Merci ou merci beaucoup ?", a: "« Merci » suffit dans 90 % des cas. Utilise « merci beaucoup » pour insister (grand service, compliment reçu). « Je vous remercie » réservé au très formel." },
      },
      q2: {
        pt: { q: "Como responder a um « merci » em francês?", a: "A resposta padrão é « De rien » (« De nada »). Alternativas: « Je vous en prie » (formal), « Pas de quoi » (casual), « Avec plaisir » (« Com prazer »)." },
        en: { q: "How do I reply to « merci »?", a: "The default reply is « De rien » (You're welcome). Alternatives: « Je vous en prie » (formal), « Pas de quoi » (casual), « Avec plaisir » (My pleasure)." },
        fr: { q: "Comment répondre à un « merci » ?", a: "Réponse standard : « De rien ». Variantes : « Je vous en prie » (formel), « Pas de quoi » (casual), « Avec plaisir »." },
      },
      q3: {
        pt: { q: "Como pronunciar « merci » corretamente?", a: "O R é uvular (fundo da garganta, não brasileiro « carro »). O I final é fechado e curto: « mêr-SI ». Nunca alongar como « mêr-SIIII »." },
        en: { q: "How do I pronounce « merci » properly?", a: "The R is uvular (back of the throat, not the Brazilian rolled R). The final I is short and closed: « mèr-SI ». Never stretch it into « mèr-SEE »." },
        fr: { q: "Comment prononcer « merci » correctement ?", a: "Le R est uvulaire. Le I final est fermé et court : « mèr-SI ». Ne l'étire jamais en « mèr-SIII »." },
      },
    },
  },
  {
    slug: "bom-dia",
    updated: "2026-02-24",
    pt: "bom dia",
    en: "good morning / hello",
    fr: "bonjour",
    ipa: "/bɔ̃.ʒuʁ/",
    variations: [
      { fr: "Bonjour", pt: "Bom dia / olá (formal e neutro, o dia inteiro)", en: "Good morning / hello (formal, all day)", ipa: "/bɔ̃.ʒuʁ/" },
      { fr: "Salut", pt: "Oi (só com amigos)", en: "Hi (friends only)", ipa: "/sa.ly/" },
      { fr: "Bonsoir", pt: "Boa noite (a partir das 18h)", en: "Good evening (from 6 pm)", ipa: "/bɔ̃.swaʁ/" },
      { fr: "Coucou", pt: "Oioi (muito íntimo)", en: "Hey (very intimate)", ipa: "/ku.ku/" },
      { fr: "Bonne matinée", pt: "Bom dia (ao SAIR de manhã)", en: "Have a good morning (leaving)", ipa: "/bɔn ma.ti.ne/" },
    ],
    faq: {
      q1: {
        pt: { q: "Bonjour vale o dia todo?", a: "Sim, de manhã até ~18h. Depois disso, use « Bonsoir ». Diferente do português, « Bonjour » não é só « bom dia » — é o cumprimento padrão. Iniciar TODA interação com ele é a regra #1 de etiqueta francesa." },
        en: { q: "Does « Bonjour » work all day?", a: "Yes, from morning until ~6 pm. After that, use « Bonsoir ». Unlike English « good morning », « Bonjour » is the default greeting, not time-restricted. Starting every interaction with it is French etiquette rule #1." },
        fr: { q: "« Bonjour » se dit-il toute la journée ?", a: "Oui, du matin jusqu'à ~18h. Après, on passe à « Bonsoir ». C'est la salutation par défaut : commencer TOUTE interaction par « Bonjour » est la règle n° 1 de politesse française." },
      },
      q2: {
        pt: { q: "Salut é rude?", a: "Não, é apenas informal. Use com amigos, colegas próximos e crianças. NUNCA com um vendedor, garçom, funcionário público ou desconhecido — para eles é « Bonjour »." },
        en: { q: "Is « Salut » rude?", a: "No, it's just informal. Use with friends, close colleagues and kids. NEVER with a shopkeeper, waiter, civil servant or stranger — for them, it's « Bonjour »." },
        fr: { q: "« Salut » est-il impoli ?", a: "Non, juste familier. À utiliser avec amis, collègues proches, enfants. JAMAIS avec un commerçant, serveur, agent public ou inconnu — pour eux c'est « Bonjour »." },
      },
      q3: {
        pt: { q: "Como pronunciar « Bonjour »?", a: "Duas partes: « BON » (nasal, quase « bô » mas com o ar saindo pelo nariz) + « JOUR » (som do J francês, como no português « já », não como o J inglês). Nasal + J francês = 90% do trabalho." },
        en: { q: "How to pronounce « Bonjour »?", a: "Two parts: « BON » (nasal, almost « bô » with air through the nose) + « JOUR » (French J sound, like Portuguese « já », not English J). Nasal + French J = 90 % of the work." },
        fr: { q: "Comment prononcer « Bonjour » ?", a: "Deux parties : « BON » (voyelle nasale) + « JOUR » (J français, comme dans « je »). Nasale + J = 90 % du boulot." },
      },
    },
  },
  {
    slug: "desculpa",
    updated: "2026-02-24",
    pt: "desculpa",
    en: "sorry / excuse me",
    fr: "pardon / excusez-moi",
    ipa: "/paʁ.dɔ̃/",
    variations: [
      { fr: "Pardon", pt: "Desculpa (curto, universal)", en: "Sorry / pardon (short, universal)", ipa: "/paʁ.dɔ̃/" },
      { fr: "Excusez-moi", pt: "Com licença / desculpe (formal)", en: "Excuse me (formal)", ipa: "/ɛks.ky.ze mwa/" },
      { fr: "Je suis désolé(e)", pt: "Eu sinto muito (arrependimento real)", en: "I'm sorry (genuine regret)", ipa: "/ʒə sɥi de.zɔ.le/" },
      { fr: "Désolé", pt: "Desculpa (casual, sozinho)", en: "Sorry (casual, standalone)", ipa: "/de.zɔ.le/" },
      { fr: "Toutes mes excuses", pt: "Minhas sinceras desculpas (formal, escrito)", en: "My sincere apologies (formal, written)", ipa: "/tut me zɛks.kyz/" },
    ],
    faq: {
      q1: {
        pt: { q: "Pardon ou excusez-moi?", a: "« Pardon » = você esbarrou em alguém, quer passar, ou não ouviu. « Excusez-moi » = você vai INTERROMPER (chamar garçom, pedir informação). « Je suis désolé » = você fez algo errado e lamenta." },
        en: { q: "Pardon or excusez-moi?", a: "« Pardon » = you bumped into someone, want to pass, or didn't hear. « Excusez-moi » = you're about to INTERRUPT (call waiter, ask for info). « Je suis désolé » = you did something wrong and regret it." },
        fr: { q: "Pardon ou excusez-moi ?", a: "« Pardon » = tu as bousculé quelqu'un, tu veux passer, tu n'as pas entendu. « Excusez-moi » = tu vas INTERROMPRE (appeler serveur, demander info). « Je suis désolé » = tu regrettes." },
      },
      q2: {
        pt: { q: "Como pedir alguém pra repetir o que disse?", a: "« Pardon ? » (tom subindo, curtinho). Ou « Excusez-moi, pouvez-vous répéter ? ». Evite « Quoi ? » — é rude fora de contexto muito íntimo." },
        en: { q: "How do I ask someone to repeat?", a: "« Pardon ? » (rising tone, short). Or « Excusez-moi, pouvez-vous répéter ? ». Avoid « Quoi ? » — rude outside very intimate contexts." },
        fr: { q: "Comment demander de répéter ?", a: "« Pardon ? » (ton montant). Ou « Excusez-moi, pouvez-vous répéter ? ». Évite « Quoi ? » — impoli hors contexte intime." },
      },
    },
  },
  {
    slug: "tchau",
    updated: "2026-02-24",
    pt: "tchau",
    en: "bye",
    fr: "au revoir",
    ipa: "/o ʁə.vwaʁ/",
    variations: [
      { fr: "Au revoir", pt: "Até logo (neutro, sempre correto)", en: "Goodbye (neutral, always correct)", ipa: "/o ʁə.vwaʁ/" },
      { fr: "Salut", pt: "Tchau (só com amigos)", en: "Bye (friends only)", ipa: "/sa.ly/" },
      { fr: "À bientôt", pt: "Até logo / até em breve", en: "See you soon", ipa: "/a bjɛ̃.to/" },
      { fr: "À plus", pt: "Até (super casual)", en: "See ya (super casual)", ipa: "/a plys/" },
      { fr: "Bonne journée", pt: "Bom dia (ao SAIR / se despedir)", en: "Have a good day (parting)", ipa: "/bɔn ʒuʁ.ne/" },
    ],
    faq: {
      q1: {
        pt: { q: "Como se despedir educadamente em um comércio francês?", a: "Sempre feche a interação com « Merci, bonne journée ! » (de dia) ou « Merci, bonne soirée ! » (a partir das 18h). NUNCA saia sem cumprimentar — é considerado extremamente mal-educado na França." },
        en: { q: "How do I say goodbye politely in a French shop?", a: "Always close the interaction with « Merci, bonne journée ! » (day) or « Merci, bonne soirée ! » (from 6 pm). NEVER leave without saying it — considered extremely rude in France." },
        fr: { q: "Comment dire au revoir poliment en boutique ?", a: "Toujours fermer par « Merci, bonne journée ! » (jour) ou « Merci, bonne soirée ! » (à partir de 18h). Ne jamais partir sans le dire — considéré très impoli." },
      },
      q2: {
        pt: { q: "« Ciao » funciona na França?", a: "Sim, entre amigos jovens, principalmente em contexto italiano-francês. Mas em ambiente profissional ou com desconhecidos, prefira « Au revoir »." },
        en: { q: "Does « Ciao » work in France?", a: "Yes, among young friends, especially with Italian-French vibes. But in professional contexts or with strangers, stick to « Au revoir »." },
        fr: { q: "« Ciao » fonctionne-t-il en France ?", a: "Oui, entre jeunes amis. En pro ou avec inconnus, préfère « Au revoir »." },
      },
    },
  },
  {
    slug: "eu-te-amo",
    updated: "2026-02-24",
    pt: "eu te amo",
    en: "I love you",
    fr: "je t'aime",
    ipa: "/ʒə tɛm/",
    variations: [
      { fr: "Je t'aime", pt: "Eu te amo (parceiro/família próxima)", en: "I love you (partner/close family)", ipa: "/ʒə tɛm/" },
      { fr: "Je t'aime bien", pt: "Eu gosto de você (amigo!)", en: "I like you (friend, NOT romantic)", ipa: "/ʒə tɛm bjɛ̃/" },
      { fr: "Je t'adore", pt: "Eu te adoro (afeto forte)", en: "I adore you (strong affection)", ipa: "/ʒə ta.dɔʁ/" },
      { fr: "Je vous aime", pt: "Eu vos amo (formal, dedicatória, plural)", en: "I love you (formal/plural)", ipa: "/ʒə vu zɛm/" },
    ],
    faq: {
      q1: {
        pt: { q: "Qual a diferença entre « je t'aime » e « je t'aime bien »?", a: "Enorme e crítica: « Je t'aime » = amor romântico/familiar. « Je t'aime BIEN » = eu gosto de você como amigo, NÃO tem carga romântica. Nunca use « bien » se quer se declarar!" },
        en: { q: "Difference between « je t'aime » and « je t'aime bien »?", a: "Huge and critical: « Je t'aime » = romantic/family love. « Je t'aime BIEN » = I like you as a friend, ZERO romance. Never use « bien » if you're trying to confess!" },
        fr: { q: "Différence entre « je t'aime » et « je t'aime bien » ?", a: "Énorme : « Je t'aime » = amour romantique/familial. « Je t'aime BIEN » = amitié, aucune charge romantique." },
      },
      q2: {
        pt: { q: "Quando os franceses dizem « je t'aime »?", a: "Culturalmente, o francês diz mais tarde no relacionamento que o brasileiro (a partir de 2-3 meses). Antes disso, « tu me plais » (« gosto de você »), « je pense à toi » (« penso em você »)." },
        en: { q: "When do French people say « je t'aime »?", a: "Culturally, later in the relationship than Brazilians (from 2-3 months). Before that: « tu me plais » (I fancy you), « je pense à toi » (I think of you)." },
        fr: { q: "Quand disent-ils « je t'aime » en France ?", a: "Plutôt après 2-3 mois de relation. Avant : « tu me plais », « je pense à toi »." },
      },
    },
  },
  {
    slug: "quanto-custa",
    updated: "2026-02-24",
    pt: "quanto custa",
    en: "how much does it cost",
    fr: "c'est combien / combien ça coûte",
    ipa: "/sɛ kɔ̃.bjɛ̃/",
    variations: [
      { fr: "C'est combien ?", pt: "Quanto custa? (casual, curto)", en: "How much? (casual)", ipa: "/sɛ kɔ̃.bjɛ̃/" },
      { fr: "Combien ça coûte ?", pt: "Quanto isso custa? (neutro)", en: "How much does it cost?", ipa: "/kɔ̃.bjɛ̃ sa kut/" },
      { fr: "Quel est le prix ?", pt: "Qual é o preço? (formal)", en: "What is the price? (formal)", ipa: "/kɛl ɛ lə pʁi/" },
      { fr: "Ça fait combien ?", pt: "Fica quanto? (no caixa)", en: "How much is it? (at checkout)", ipa: "/sa fɛ kɔ̃.bjɛ̃/" },
    ],
    faq: {
      q1: {
        pt: { q: "Como pedir pra escrever o preço se não entender?", a: "« Pouvez-vous l'écrire, s'il vous plaît ? » — « Pode escrever, por favor? ». Ou aponte a calculadora do celular. Números em francês são traiçoeiros (soixante-dix, quatre-vingt-dix)." },
        en: { q: "How do I ask them to write down the price?", a: "« Pouvez-vous l'écrire, s'il vous plaît ? » — « Can you write it down please? ». Or hand them your phone calculator. French numbers are tricky (soixante-dix, quatre-vingt-dix)." },
        fr: { q: "Comment demander d'écrire le prix ?", a: "« Pouvez-vous l'écrire, s'il vous plaît ? ». Ou tends la calculatrice du téléphone." },
      },
      q2: {
        pt: { q: "Dá pra pechinchar na França?", a: "Em lojas normais e supermercados: NÃO. Em brechós (marché aux puces), mercados de rua, feiras de artesanato: sim, mas com moderação — não regatee agressivamente como no Brasil. Use « Vous pouvez faire un geste ? »." },
        en: { q: "Can I haggle in France?", a: "In shops and supermarkets: NO. In flea markets (marché aux puces), street markets, craft fairs: yes, but with restraint. Use « Vous pouvez faire un geste ? » (Can you meet me halfway?)." },
        fr: { q: "Peut-on marchander en France ?", a: "En boutique/supermarché : non. Aux puces, marchés, foires artisanales : oui, avec modération. « Vous pouvez faire un geste ? »." },
      },
    },
  },
  {
    slug: "onde-fica",
    updated: "2026-02-24",
    pt: "onde fica",
    en: "where is",
    fr: "où est / où se trouve",
    ipa: "/u ɛ/",
    variations: [
      { fr: "Où est... ?", pt: "Onde é / onde fica...?", en: "Where is...?", ipa: "/u ɛ/" },
      { fr: "Où se trouve... ?", pt: "Onde se encontra...? (formal)", en: "Where is located...? (formal)", ipa: "/u sə tʁuv/" },
      { fr: "C'est où, ... ?", pt: "Onde é...? (super casual)", en: "Where is it, ...? (very casual)", ipa: "/sɛ tu/" },
      { fr: "Comment aller à... ?", pt: "Como chegar em...?", en: "How to get to...?", ipa: "/kɔ.mɑ̃ ta.le a/" },
    ],
    faq: {
      q1: {
        pt: { q: "Como perguntar onde fica o banheiro?", a: "« Où sont les toilettes, s'il vous plaît ? » — atenção, é PLURAL (« les toilettes »). No singular soa estranho. Em contexto formal: « Où puis-je trouver les toilettes ? »." },
        en: { q: "How do I ask where the toilet is?", a: "« Où sont les toilettes, s'il vous plaît ? » — note it's PLURAL. Singular sounds odd. Formal: « Où puis-je trouver les toilettes ? »." },
        fr: { q: "Comment demander où sont les toilettes ?", a: "« Où sont les toilettes, s'il vous plaît ? » — attention, pluriel obligatoire." },
      },
      q2: {
        pt: { q: "Como pedir direção específica na rua?", a: "« Excusez-moi, pouvez-vous m'indiquer le chemin pour aller à [lugar] ? ». Ou mostre o mapa: « Je cherche [X], c'est loin ? »." },
        en: { q: "How do I ask for specific directions?", a: "« Excusez-moi, pouvez-vous m'indiquer le chemin pour aller à [place] ? ». Or show the map: « Je cherche [X], c'est loin ? »." },
        fr: { q: "Comment demander une direction précise ?", a: "« Excusez-moi, pouvez-vous m'indiquer le chemin pour... ? ». Ou : « Je cherche X, c'est loin ? »." },
      },
    },
  },
  {
    slug: "estou-perdido",
    updated: "2026-02-24",
    pt: "estou perdido",
    en: "I'm lost",
    fr: "je suis perdu(e)",
    ipa: "/ʒə sɥi pɛʁ.dy/",
    variations: [
      { fr: "Je suis perdu (m.) / perdue (f.)", pt: "Eu estou perdido / perdida", en: "I'm lost", ipa: "/ʒə sɥi pɛʁ.dy/" },
      { fr: "Je me suis perdu(e)", pt: "Eu me perdi", en: "I got lost", ipa: "/ʒə mə sɥi pɛʁ.dy/" },
      { fr: "J'ai perdu mon chemin", pt: "Perdi meu caminho", en: "I've lost my way", ipa: "/ʒe pɛʁ.dy mɔ̃ ʃə.mɛ̃/" },
      { fr: "Pouvez-vous m'aider ?", pt: "Você pode me ajudar?", en: "Can you help me?", ipa: "/pu.ve vu mɛ.de/" },
    ],
    faq: {
      q1: {
        pt: { q: "Frase completa pra pedir ajuda?", a: "« Excusez-moi, je suis perdu(e), pouvez-vous m'aider ? ». Comece SEMPRE com « Excusez-moi » (ou « Bonjour » se for antes de qualquer contato). Franceses ajudam se você começar com cortesia." },
        en: { q: "Full phrase to ask for help?", a: "« Excusez-moi, je suis perdu(e), pouvez-vous m'aider ? ». ALWAYS start with « Excusez-moi » (or « Bonjour » if you haven't greeted them yet). French people help when you start politely." },
        fr: { q: "Phrase complète pour demander de l'aide ?", a: "« Excusez-moi, je suis perdu(e), pouvez-vous m'aider ? ». Commence toujours par « Excusez-moi » ou « Bonjour »." },
      },
    },
  },
  {
    slug: "a-conta-por-favor",
    updated: "2026-02-24",
    pt: "a conta por favor",
    en: "the bill please",
    fr: "l'addition s'il vous plaît",
    ipa: "/la.di.sjɔ̃ sil vu plɛ/",
    variations: [
      { fr: "L'addition, s'il vous plaît", pt: "A conta, por favor", en: "The bill, please", ipa: "/la.di.sjɔ̃/" },
      { fr: "On peut avoir l'addition ?", pt: "Podemos ter a conta?", en: "Can we have the bill?", ipa: "/ɔ̃ pø a.vwaʁ/" },
      { fr: "Vous pouvez nous apporter l'addition ?", pt: "Você pode nos trazer a conta? (formal)", en: "Can you bring us the bill?", ipa: "/vu pu.ve nu za.pɔʁ.te/" },
    ],
    faq: {
      q1: {
        pt: { q: "Preciso chamar o garçom pra pedir a conta?", a: "Sim, na França o garçom NUNCA traz a conta espontaneamente — sinal de que ele te apressaria. Levante a mão, cruze o olhar e diga « L'addition, s'il vous plaît ! » ou faça o gesto de assinar no ar." },
        en: { q: "Do I need to flag the waiter for the bill?", a: "Yes, in France the waiter NEVER brings the bill on their own — that would rush you. Raise your hand, catch their eye, say « L'addition, s'il vous plaît ! » or mime signing in the air." },
        fr: { q: "Faut-il appeler le serveur pour l'addition ?", a: "Oui, en France le serveur n'apporte jamais l'addition spontanément. Signale par un geste, un regard, ou dis « L'addition, s'il vous plaît ! »." },
      },
      q2: {
        pt: { q: "Gorjeta é obrigatória?", a: "Não. O serviço já vem incluído por lei (« service compris »). Deixar 1-3€ em dinheiro se gostou muito é gesto simpático, mas NÃO é uma obrigação de 10-15% como nos EUA." },
        en: { q: "Is tipping mandatory?", a: "No. Service is already included by law (« service compris »). Leaving 1-3€ in cash if you loved it is a nice gesture, but NOT a 10-15 % obligation like the US." },
        fr: { q: "Le pourboire est-il obligatoire ?", a: "Non, le service est compris. Laisser 1-3€ si tu as apprécié est un geste sympa, pas une obligation." },
      },
    },
  },
  {
    slug: "voce-fala-portugues",
    updated: "2026-02-24",
    pt: "você fala português",
    en: "do you speak Portuguese",
    fr: "parlez-vous portugais",
    ipa: "/paʁ.le vu pɔʁ.ty.ɡɛ/",
    variations: [
      { fr: "Parlez-vous portugais ?", pt: "Você fala português? (formal)", en: "Do you speak Portuguese? (formal)", ipa: "/paʁ.le vu/" },
      { fr: "Tu parles portugais ?", pt: "Você fala português? (íntimo)", en: "Do you speak Portuguese? (intimate)", ipa: "/ty paʁl/" },
      { fr: "Parlez-vous anglais ?", pt: "Você fala inglês?", en: "Do you speak English?", ipa: "/paʁ.le vu zɑ̃.ɡlɛ/" },
      { fr: "Je ne parle pas français", pt: "Eu não falo francês", en: "I don't speak French", ipa: "/ʒə nə paʁl pa fʁɑ̃.sɛ/" },
    ],
    faq: {
      q1: {
        pt: { q: "Falam português na França?", a: "Comunidade portuguesa é enorme na França (~1.5 milhão). Em Paris e Lyon, muitos garçons, motoristas, funcionários falam português. Mas comece SEMPRE com « Bonjour ! Parlez-vous portugais ? » — nunca em português direto." },
        en: { q: "Do people speak Portuguese in France?", a: "Portuguese community in France is huge (~1.5 M). In Paris and Lyon many waiters, drivers, staff speak Portuguese. But ALWAYS start with « Bonjour ! Parlez-vous portugais ? » — never in Portuguese directly." },
        fr: { q: "Parle-t-on portugais en France ?", a: "Communauté portugaise énorme (~1,5 M). À Paris et Lyon, beaucoup de serveurs, chauffeurs, employés le parlent. Commence toujours par « Bonjour ! Parlez-vous portugais ? »." },
      },
    },
  },
  {
    slug: "bom",
    updated: "2026-02-24",
    pt: "bom / boa",
    en: "good",
    fr: "bon / bonne",
    ipa: "/bɔ̃/",
    variations: [
      { fr: "Bon (m.) / Bonne (f.)", pt: "Bom / boa (adjetivo, com substantivo)", en: "Good (adjective, with noun)", ipa: "/bɔ̃/ /bɔn/" },
      { fr: "Bien", pt: "Bem (advérbio, com verbo)", en: "Well (adverb, with verb)", ipa: "/bjɛ̃/" },
      { fr: "C'est bon", pt: "Está bom / gostoso", en: "It's good / tasty", ipa: "/sɛ bɔ̃/" },
      { fr: "Bonne journée !", pt: "Bom dia! (ao sair)", en: "Have a good day!", ipa: "/bɔn ʒuʁ.ne/" },
    ],
    faq: {
      q1: {
        pt: { q: "Bon ou bien: quando usar cada um?", a: "BON = adjetivo (« un bon vin »). BIEN = advérbio (« il chante bien »). Regra: se qualifica substantivo, BON. Se qualifica verbo, BIEN. Nunca « c'est bien » quando quer dizer que a comida está gostosa — é « c'est bon »." },
        en: { q: "Bon or bien?", a: "BON = adjective (« un bon vin »). BIEN = adverb (« il chante bien »). Rule: if it qualifies a noun, BON. If a verb, BIEN. Never say « c'est bien » to mean tasty — it's « c'est bon »." },
        fr: { q: "Bon ou bien ?", a: "Bon adjectif, bien adverbe. Voir la page dédiée /frances/diferenca/bien-vs-bon." },
      },
    },
  },
  {
    slug: "tudo-bem",
    updated: "2026-02-24",
    pt: "tudo bem",
    en: "how are you / all good",
    fr: "ça va",
    ipa: "/sa va/",
    variations: [
      { fr: "Ça va ?", pt: "Tudo bem? (pergunta)", en: "How are you? (question)", ipa: "/sa va/" },
      { fr: "Ça va, merci", pt: "Tudo bem, obrigado (resposta neutra)", en: "I'm fine, thanks", ipa: "/sa va mɛʁ.si/" },
      { fr: "Comment allez-vous ?", pt: "Como vai? (formal)", en: "How are you? (formal)", ipa: "/kɔ.mɑ̃ ta.le vu/" },
      { fr: "Ça roule ?", pt: "Tudo tranquilo? (casual)", en: "All good? (casual)", ipa: "/sa ʁul/" },
    ],
    faq: {
      q1: {
        pt: { q: "Como responder « Ça va ? »", a: "« Ça va, merci ! Et vous ? » (« et toi ? » se for íntimo). Nunca fique só em « ça va » sem retornar a pergunta — é considerado seco. Se está mal: « Bof, ça va » (« mais ou menos »)." },
        en: { q: "How to answer « Ça va ? »", a: "« Ça va, merci ! Et vous ? » (« et toi ? » if intimate). Never just say « ça va » without returning the question — considered curt. If not great: « Bof, ça va » (« so-so »)." },
        fr: { q: "Comment répondre à « Ça va ? »", a: "« Ça va, merci ! Et vous ? » Retourne toujours la question." },
      },
    },
  },
  {
    slug: "por-favor",
    updated: "2026-02-24",
    pt: "por favor",
    en: "please",
    fr: "s'il vous plaît",
    ipa: "/sil vu plɛ/",
    variations: [
      { fr: "S'il vous plaît", pt: "Por favor (formal / neutro)", en: "Please (formal / neutral)", ipa: "/sil vu plɛ/" },
      { fr: "S'il te plaît", pt: "Por favor (íntimo, com amigo)", en: "Please (intimate)", ipa: "/sil tə plɛ/" },
      { fr: "Je vous en prie", pt: "Eu vos peço (super formal)", en: "I beg you (very formal)", ipa: "/ʒə vu zɑ̃ pʁi/" },
    ],
    faq: {
      q1: {
        pt: { q: "Sempre « vous » em « s'il vous plaît »?", a: "Não. Em contexto íntimo, use « s'il te plaît ». Regra: se você trata a pessoa por TU, é « s'il te plaît ». Se por VOUS (que é o padrão com desconhecido), é « s'il vous plaît »." },
        en: { q: "Always « vous » in « s'il vous plaît »?", a: "No. In intimate context, use « s'il te plaît ». Rule: if you address the person as TU, it's « s'il te plaît ». If VOUS (default with strangers), it's « s'il vous plaît »." },
        fr: { q: "Toujours « vous » dans « s'il vous plaît » ?", a: "Non — s'accorde au TU/VOUS choisi." },
      },
    },
  },
  {
    slug: "prazer-em-conhecer",
    updated: "2026-02-24",
    pt: "prazer em conhecer",
    en: "nice to meet you",
    fr: "enchanté",
    ipa: "/ɑ̃.ʃɑ̃.te/",
    variations: [
      { fr: "Enchanté (m.) / Enchantée (f.)", pt: "Prazer (formal, ao apresentar)", en: "Nice to meet you (formal)", ipa: "/ɑ̃.ʃɑ̃.te/" },
      { fr: "Ravi de vous rencontrer", pt: "Muito prazer em conhecer (bem formal)", en: "Delighted to meet you (very formal)", ipa: "/ʁa.vi/" },
      { fr: "Content de te voir", pt: "Feliz em te ver (íntimo)", en: "Glad to see you (intimate)", ipa: "/kɔ̃.tɑ̃/" },
    ],
    faq: {
      q1: {
        pt: { q: "Enchanté ou Enchantée: como saber?", a: "Concorda com QUEM FALA. Homem: « enchanté ». Mulher: « enchantée ». Escrita: E adicional no fim. Fala: som é IDÊNTICO — só existe diferença ortográfica." },
        en: { q: "Enchanté or Enchantée?", a: "Agrees with the SPEAKER's gender. Male: « enchanté ». Female: « enchantée ». Written: extra E. Spoken: IDENTICAL sound — only spelling differs." },
        fr: { q: "Enchanté ou enchantée ?", a: "S'accorde au locuteur : masculin/féminin. Prononcé pareil." },
      },
    },
  },
  {
    slug: "saude",
    updated: "2026-02-24",
    pt: "saúde (brindar)",
    en: "cheers",
    fr: "santé",
    ipa: "/sɑ̃.te/",
    variations: [
      { fr: "Santé !", pt: "Saúde! (brinde)", en: "Cheers!", ipa: "/sɑ̃.te/" },
      { fr: "Tchin-tchin !", pt: "Tim-tim! (bem casual)", en: "Chin-chin! (casual)", ipa: "/tʃin tʃin/" },
      { fr: "À la vôtre !", pt: "À vossa! (formal, no plural)", en: "To yours! (formal)", ipa: "/a la votʁ/" },
      { fr: "À la tienne !", pt: "À sua! (íntimo)", en: "To yours! (intimate)", ipa: "/a la tjɛn/" },
    ],
    faq: {
      q1: {
        pt: { q: "Precisa olhar nos olhos ao brindar?", a: "SIM, e é sério na França. Não olhar nos olhos ao dizer « santé ! » traz azar por 7 anos (superstição forte). Também: bater o copo ao dizer, não antes nem depois. Ritual bastante rígido." },
        en: { q: "Do I need eye contact when toasting?", a: "YES, and it's serious in France. Not making eye contact when saying « santé ! » brings 7 years of bad luck (strong superstition). Also: clink the glass while saying it, not before or after. Quite rigid ritual." },
        fr: { q: "Faut-il se regarder dans les yeux en trinquant ?", a: "Oui, sinon 7 ans de malheur (superstition forte)." },
      },
    },
  },
  {
    slug: "boa-sorte",
    updated: "2026-02-24",
    pt: "boa sorte",
    en: "good luck",
    fr: "bonne chance",
    ipa: "/bɔn ʃɑ̃s/",
    variations: [
      { fr: "Bonne chance !", pt: "Boa sorte! (universal)", en: "Good luck!", ipa: "/bɔn ʃɑ̃s/" },
      { fr: "Merde !", pt: "Boa sorte! (teatro/exame, literal « merda »)", en: "Good luck! (theatre/exam)", ipa: "/mɛʁd/" },
      { fr: "Je te souhaite bonne chance", pt: "Eu te desejo boa sorte", en: "I wish you good luck", ipa: "/swɛt/" },
    ],
    faq: {
      q1: {
        pt: { q: "Por que os franceses dizem « merde ! » pra desejar sorte?", a: "Superstição teatral: dizer « bonne chance » no teatro traz azar. « Merde ! » é o antídoto — tradição desde o século XIX. Usa-se também pra exames importantes. Não é palavrão nesse contexto." },
        en: { q: "Why do French say « merde ! » for good luck?", a: "Theatrical superstition: saying « bonne chance » before a show brings bad luck. « Merde ! » is the antidote — 19th-century tradition. Also used for important exams. Not vulgar in this context." },
        fr: { q: "Pourquoi dire « merde » pour souhaiter bonne chance ?", a: "Superstition théâtrale XIXe siècle. Utilisé aussi avant un examen important." },
      },
    },
  },
  { slug: "como-vai", updated: "2026-02-24", pt: "como vai", en: "how are you", fr: "comment allez-vous", ipa: "/kɔ.mɑ̃ ta.le vu/",
    variations: [
      { fr: "Comment allez-vous ?", pt: "Como vai o senhor/a? (formal)", en: "How are you? (formal)", ipa: "/kɔ.mɑ̃ ta.le vu/" },
      { fr: "Comment tu vas ?", pt: "Como você vai? (íntimo)", en: "How are you? (intimate)", ipa: "/kɔ.mɑ̃ ty va/" },
      { fr: "Comment ça va ?", pt: "Como vai? (neutro)", en: "How's it going?", ipa: "/kɔ.mɑ̃ sa va/" },
    ],
    faq: { q1: {
      pt: { q: "Comment allez-vous vs Ça va: quando usar cada?", a: "« Comment allez-vous ? » é formal (novo cliente, autoridade). « Ça va ? » é o padrão neutro do dia-a-dia. « Comment tu vas ? » só quem você tutoya. Se em dúvida, comece com « Comment allez-vous ? » — mais seguro." },
      en: { q: "Comment allez-vous vs Ça va: which one?", a: "« Comment allez-vous ? » is formal (new client, authority). « Ça va ? » is the neutral daily default. « Comment tu vas ? » only with people you TU. When in doubt, start with « Comment allez-vous ? »." },
      fr: { q: "Comment allez-vous ou ça va ?", a: "Le formel demande « Comment allez-vous ? », l'usage quotidien « Ça va ? »." },
    } },
  },
  { slug: "ate-logo", updated: "2026-02-24", pt: "até logo", en: "see you later", fr: "à plus tard", ipa: "/a ply taʁ/",
    variations: [
      { fr: "À plus tard", pt: "Até logo (mesmo dia)", en: "See you later (same day)", ipa: "/a ply taʁ/" },
      { fr: "À demain", pt: "Até amanhã", en: "See you tomorrow", ipa: "/a də.mɛ̃/" },
      { fr: "À la prochaine", pt: "Até a próxima", en: "Until next time", ipa: "/a la pʁɔ.ʃɛn/" },
      { fr: "À plus", pt: "Até (super casual, SMS)", en: "See ya (super casual, SMS)", ipa: "/a plys/" },
    ],
    faq: { q1: {
      pt: { q: "« À plus tard » ou « à plus »?", a: "« À plus tard » é neutro, fala e escrita. « À plus » (com S sonoro em « plyss ») é super casual, entre amigos e SMS. « À plus » sem contexto pode soar seco em ambiente profissional." },
      en: { q: "« À plus tard » or « à plus »?", a: "« À plus tard » is neutral, spoken and written. « À plus » (with pronounced S) is super casual, among friends and in SMS. In professional context, « à plus » alone can sound curt." },
      fr: { q: "À plus tard ou à plus ?", a: "« À plus » est très familier ; « à plus tard » neutre." },
    } },
  },
  { slug: "eu-nao-sei", updated: "2026-02-24", pt: "eu não sei", en: "I don't know", fr: "je ne sais pas", ipa: "/ʒə nə sɛ pa/",
    variations: [
      { fr: "Je ne sais pas", pt: "Eu não sei (padrão escrito)", en: "I don't know (written)", ipa: "/ʒə nə sɛ pa/" },
      { fr: "Chais pas", pt: "Sei lá (oral, muito comum)", en: "Dunno (very oral)", ipa: "/ʃɛ pa/" },
      { fr: "Aucune idée", pt: "Nenhuma ideia", en: "No idea", ipa: "/o.kyn i.de/" },
      { fr: "Je n'en sais rien", pt: "Não faço a mínima ideia", en: "Haven't the faintest idea", ipa: "/ʒə nɑ̃ sɛ ʁjɛ̃/" },
    ],
    faq: { q1: {
      pt: { q: "« Chais pas » é gíria?", a: "É oral, não gíria. Fala corrente parisiense contrai « je ne sais pas » em « chais pas » 90% do tempo. Aceito em qualquer contexto oral, mesmo profissional. Só evite em escrita formal." },
      en: { q: "Is « chais pas » slang?", a: "Oral, not slang. Casual Parisian speech contracts « je ne sais pas » into « chais pas » 90 % of the time. Fine in any spoken context, even at work. Just avoid in formal writing." },
      fr: { q: "« Chais pas » est-il de l'argot ?", a: "Non, français oral courant. À éviter uniquement à l'écrit formel." },
    } },
  },
  { slug: "me-ajuda", updated: "2026-02-24", pt: "me ajuda", en: "help me", fr: "aide-moi / aidez-moi", ipa: "/ɛ.de mwa/",
    variations: [
      { fr: "Aidez-moi, s'il vous plaît", pt: "Me ajuda, por favor (formal)", en: "Help me, please (formal)", ipa: "/ɛ.de mwa/" },
      { fr: "Aide-moi", pt: "Me ajuda (íntimo)", en: "Help me (intimate)", ipa: "/ɛd mwa/" },
      { fr: "Au secours !", pt: "Socorro! (emergência)", en: "Help! (emergency)", ipa: "/o sə.kuʁ/" },
      { fr: "Pouvez-vous m'aider ?", pt: "Você pode me ajudar? (formal)", en: "Can you help me? (formal)", ipa: "/pu.ve vu mɛ.de/" },
    ],
    faq: { q1: {
      pt: { q: "Quando gritar « Au secours ! »?", a: "Só em emergência real: assalto, acidente, alguém passando mal. « Au secours ! » é o « socorro! » francês. Fora emergência, use « Pouvez-vous m'aider ? » — muito mais aceito socialmente." },
      en: { q: "When to shout « Au secours ! »?", a: "Only in a real emergency: assault, accident, medical crisis. « Au secours ! » is French « help! ». Outside emergencies, use « Pouvez-vous m'aider ? » — far more socially accepted." },
      fr: { q: "Quand crier « Au secours » ?", a: "Uniquement en urgence réelle. Sinon : « Pouvez-vous m'aider ? »." },
    } },
  },
  { slug: "entao", updated: "2026-02-24", pt: "então", en: "so / then", fr: "donc / alors / du coup", ipa: "/dɔ̃k/",
    variations: [
      { fr: "Donc", pt: "Então (lógico/conclusão)", en: "So (logical/conclusion)", ipa: "/dɔ̃k/" },
      { fr: "Alors", pt: "Então (transição/narrativa)", en: "So (transition/narrative)", ipa: "/a.lɔʁ/" },
      { fr: "Du coup", pt: "Aí (oral, muito frequente)", en: "So (very oral, frequent)", ipa: "/dy ku/" },
      { fr: "En fait", pt: "Na verdade (correção)", en: "Actually (correction)", ipa: "/ɑ̃ fɛt/" },
    ],
    faq: { q1: {
      pt: { q: "Donc, alors ou du coup?", a: "« Donc » = conclusão lógica (« Il pleut, donc je reste »). « Alors » = transição narrativa (« Alors je lui ai dit... »). « Du coup » = consequência oral popular (« J'ai raté le train, du coup je suis en retard »). Franceses jovens usam « du coup » constantemente." },
      en: { q: "Donc, alors or du coup?", a: "« Donc » = logical conclusion (« It's raining, donc I stay »). « Alors » = narrative transition (« Alors I told him... »). « Du coup » = oral popular consequence. Young French use « du coup » constantly." },
      fr: { q: "Donc, alors ou du coup ?", a: "Donc = conclusion logique. Alors = transition. Du coup = conséquence orale familière." },
    } },
  },
  { slug: "quero-um-cafe", updated: "2026-02-24", pt: "quero um café", en: "I'd like a coffee", fr: "je voudrais un café", ipa: "/ʒə vu.dʁɛ ɛ̃ ka.fe/",
    variations: [
      { fr: "Je voudrais un café, s'il vous plaît", pt: "Eu gostaria de um café, por favor", en: "I'd like a coffee, please", ipa: "/ʒə vu.dʁɛ/" },
      { fr: "Un café, s'il vous plaît", pt: "Um café, por favor (mais direto)", en: "A coffee, please (direct)", ipa: "/ɛ̃ ka.fe/" },
      { fr: "Un expresso", pt: "Um espresso (o padrão francês)", en: "An espresso (French default)", ipa: "/ɛks.pʁɛ.so/" },
      { fr: "Un café allongé", pt: "Um café diluído (parecido com « americano »)", en: "A long coffee (like « americano »)", ipa: "/a.lɔ̃.ʒe/" },
    ],
    faq: { q1: {
      pt: { q: "« Un café » é expresso ou coado?", a: "Expresso, sempre. Na França, « un café » = espresso curto, 5-7 cl. Se quer diluído, peça « un café allongé ». Café coado brasileiro (« filtre ») é raro nos bistrôs; existe mais em cafeterias especializadas." },
      en: { q: "Is « un café » espresso or filter?", a: "Espresso, always. In France, « un café » = short espresso, 5-7 cl. For diluted, ask « un café allongé ». Brazilian filter coffee is rare in bistrots; more common in specialty coffee shops." },
      fr: { q: "« Un café » = expresso ?", a: "Oui, un expresso court. Allongé si tu veux dilué." },
    } },
  },
  { slug: "estou-com-fome", updated: "2026-02-24", pt: "estou com fome", en: "I'm hungry", fr: "j'ai faim", ipa: "/ʒe fɛ̃/",
    variations: [
      { fr: "J'ai faim", pt: "Estou com fome (literal: « tenho fome »)", en: "I'm hungry (literal: « I have hunger »)", ipa: "/ʒe fɛ̃/" },
      { fr: "J'ai soif", pt: "Estou com sede", en: "I'm thirsty", ipa: "/ʒe swaf/" },
      { fr: "Je meurs de faim", pt: "Estou morrendo de fome", en: "I'm starving", ipa: "/ʒə mœʁ də fɛ̃/" },
      { fr: "J'ai un petit creux", pt: "Estou com uma fominha", en: "I feel a little peckish", ipa: "/ʒe ɛ̃ pə.ti kʁø/" },
    ],
    faq: { q1: {
      pt: { q: "Por que « TER fome » em vez de « ESTAR com fome »?", a: "Estruturalmente o francês trata sensações como POSSE, não estado. J'ai faim (tenho fome), j'ai soif (tenho sede), j'ai chaud (tenho calor), j'ai froid (tenho frio), j'ai peur (tenho medo). Aprenda TODAS com « avoir » — jamais com « être »." },
      en: { q: "Why « TO HAVE hunger » instead of « TO BE hungry »?", a: "French treats sensations as POSSESSION, not state. J'ai faim, j'ai soif, j'ai chaud, j'ai froid, j'ai peur. Learn ALL of them with « avoir » — never « être »." },
      fr: { q: "Pourquoi « avoir faim » et pas « être faim » ?", a: "En français, les sensations utilisent avoir : avoir faim, soif, chaud, froid, peur." },
    } },
  },
  { slug: "estou-cansado", updated: "2026-02-24", pt: "estou cansado", en: "I'm tired", fr: "je suis fatigué(e)", ipa: "/ʒə sɥi fa.ti.ɡe/",
    variations: [
      { fr: "Je suis fatigué (m.) / fatiguée (f.)", pt: "Estou cansado / cansada", en: "I'm tired", ipa: "/fa.ti.ɡe/" },
      { fr: "Je suis crevé(e)", pt: "Estou moído/a (oral)", en: "I'm knackered (oral)", ipa: "/kʁə.ve/" },
      { fr: "Je n'en peux plus", pt: "Não aguento mais", en: "I can't take it anymore", ipa: "/ʒə nɑ̃ pø ply/" },
      { fr: "J'ai sommeil", pt: "Estou com sono", en: "I'm sleepy", ipa: "/ʒe sɔ.mɛj/" },
    ],
    faq: { q1: {
      pt: { q: "Diferença entre fatigué e crevé?", a: "« Fatigué » é neutro, sempre correto. « Crevé » é oral e mais forte (« moído », « acabado ») — usa com amigos, não com chefe. « Épuisé » é intenso e formal, tipo depois de uma maratona." },
      en: { q: "Fatigué vs crevé?", a: "« Fatigué » is neutral, always fine. « Crevé » is oral and stronger (« knackered ») — friends only, not the boss. « Épuisé » is intense and formal, like post-marathon." },
      fr: { q: "Fatigué ou crevé ?", a: "Fatigué neutre, crevé familier, épuisé intense." },
    } },
  },
  { slug: "gosto-de-voce", updated: "2026-02-24", pt: "gosto de você", en: "I like you", fr: "tu me plais", ipa: "/ty mə plɛ/",
    variations: [
      { fr: "Tu me plais", pt: "Gosto de você (levemente romântico)", en: "I like you / I fancy you", ipa: "/ty mə plɛ/" },
      { fr: "Je t'aime bien", pt: "Gosto de você como amigo (NÃO romântico)", en: "I like you as a friend (NOT romantic)", ipa: "/ʒə tɛm bjɛ̃/" },
      { fr: "Je t'apprécie", pt: "Te aprecio (afeto sem romance)", en: "I appreciate you (affection, no romance)", ipa: "/ʒə ta.pʁe.sje/" },
      { fr: "Tu me manques", pt: "Sinto sua falta (« você me falta »)", en: "I miss you (literal: « you miss me »)", ipa: "/ty mə mɑ̃k/" },
    ],
    faq: { q1: {
      pt: { q: "« Tu me plais » é uma declaração?", a: "Levemente sim. É o « paquera oficial » francês, mais ousado que « je t'apprécie » mas menos comprometido que « je t'aime ». Perfeito no início de um relacionamento pra sinalizar interesse sem pressionar." },
      en: { q: "Is « Tu me plais » a confession?", a: "Lightly yes. It's the French official-flirt phrase, bolder than « je t'apprécie » but less committed than « je t'aime ». Perfect early in a relationship to signal interest without pressure." },
      fr: { q: "« Tu me plais » est-ce une déclaration ?", a: "Un signal d'intérêt, entre l'amitié et la vraie déclaration." },
    } },
  },
  { slug: "desculpe-o-atraso", updated: "2026-02-24", pt: "desculpe o atraso", en: "sorry I'm late", fr: "désolé du retard", ipa: "/de.zɔ.le dy ʁə.taʁ/",
    variations: [
      { fr: "Désolé(e) du retard", pt: "Desculpe o atraso (neutro)", en: "Sorry I'm late (neutral)", ipa: "/dy ʁə.taʁ/" },
      { fr: "Excusez-moi pour le retard", pt: "Me desculpe pelo atraso (formal)", en: "Excuse me for the delay (formal)", ipa: "/ɛks.ky.ze/" },
      { fr: "Je suis en retard", pt: "Estou atrasado(a)", en: "I'm running late", ipa: "/ɑ̃ ʁə.taʁ/" },
      { fr: "Pardon, il y a eu du trafic", pt: "Perdão, teve trânsito (justificativa clássica)", en: "Sorry, there was traffic (classic excuse)", ipa: "/tʁa.fik/" },
    ],
    faq: { q1: {
      pt: { q: "5 minutos de atraso é aceitável em Paris?", a: "Em contexto social: sim, até 15 min. Em contexto profissional: NÃO. Chegar 5 min atrasado numa reunião de trabalho já exige desculpa. Cultura do respeito ao horário é forte fora do universo anglo-saxão descontraído." },
      en: { q: "Is 5 min late acceptable in Paris?", a: "Social context: yes, up to 15 min. Professional context: NO. Being 5 min late to a work meeting already demands an apology. Time-respect culture is stronger than the relaxed Anglo one." },
      fr: { q: "5 minutes de retard, c'est acceptable ?", a: "Social oui jusqu'à 15 min, pro non." },
    } },
  },
  { slug: "voce-e-brasileiro", updated: "2026-02-24", pt: "você é brasileiro", en: "are you Brazilian", fr: "vous êtes brésilien(ne)", ipa: "/vu zɛt bʁe.zi.ljɛ̃/",
    variations: [
      { fr: "Vous êtes brésilien(ne) ?", pt: "Você é brasileiro/a? (formal)", en: "Are you Brazilian? (formal)", ipa: "/vu zɛt/" },
      { fr: "Tu es brésilien ?", pt: "Você é brasileiro? (íntimo)", en: "Are you Brazilian? (intimate)", ipa: "/ty ɛ/" },
      { fr: "D'où venez-vous ?", pt: "De onde você vem?", en: "Where are you from?", ipa: "/du və.ne vu/" },
      { fr: "Je viens du Brésil", pt: "Eu venho do Brasil", en: "I come from Brazil", ipa: "/ʒə vjɛ̃ dy bʁe.zil/" },
    ],
    faq: { q1: {
      pt: { q: "Brésilien ou brésilienne?", a: "Brésilien = masculino. Brésilienne = feminino (com dois N e E extra). Fala: som quase idêntico, mudança sutil da vogal. Escrita: OBRIGATÓRIO usar a forma feminina se você é mulher — brasileiras que escrevem « je suis brésilien » no CV cometem erro básico." },
      en: { q: "Brésilien or brésilienne?", a: "Brésilien = masculine. Brésilienne = feminine (double N + extra E). Speech: nearly identical, subtle vowel shift. Written: MANDATORY to use the feminine form if you're a woman — Brazilian women writing « je suis brésilien » on their CV make a basic mistake." },
      fr: { q: "Brésilien ou brésilienne ?", a: "Accord obligatoire au féminin à l'écrit." },
    } },
  },
];

// ------------------------------------------------------------------
// "Pronúncia de X em francês" — 14 entries
// ------------------------------------------------------------------
export const PRONUNCIATION = [
  {
    slug: "bonjour",
    updated: "2026-02-24",
    word: "Bonjour",
    ipa: "/bɔ̃.ʒuʁ/",
    meaning: { pt: "olá / bom dia", en: "hello / good morning", fr: "salutation standard" },
    steps: {
      pt: [
        "BON — vogal nasal. Diga « bô » MAS deixe o ar sair pelo NARIZ ao mesmo tempo. Nada de « bon » com N clara.",
        "JOUR — o J francês é como o do português « já » (não como « J » inglês de « jump »). Depois « OUR » = som fechado + R uvular no fim.",
        "Junte SEM pausa: /bɔ̃.ʒuʁ/. Duração igual entre as duas sílabas. Nunca acentue o R final, ele é curto.",
      ],
      en: [
        "BON — nasal vowel. Say « bo » AND let the air out through your NOSE simultaneously. No clear N sound.",
        "JOUR — the French J is the Portuguese « já » sound (NOT English J like « jump »). Then « OUR » = closed sound + uvular R.",
        "Blend without pause: /bɔ̃.ʒuʁ/. Both syllables equal length. Never stress the final R — it's short.",
      ],
      fr: [
        "BON — voyelle nasale. Dis « bo » mais laisse l'air sortir par le nez.",
        "JOUR — le J français comme dans « je », suivi de « our » et R uvulaire.",
        "Enchaîne sans pause : /bɔ̃.ʒuʁ/, syllabes égales.",
      ],
    },
    faq: {
      pt: [
        { q: "Como fazer a vogal nasal do BON?", a: "Truque: pronuncie « bo » enquanto TAPA levemente o nariz com o dedo. Solte o nariz — você sente o ar saindo. É essa passagem nasal que falta no português brasileiro (temos nasais, mas não a nasal fechada /ɔ̃/)." },
        { q: "O R final é obrigatório?", a: "Sim, mas curto. Um R uvular breve, quase engolido. Nunca alongue: « BON-JOUUUUR » soa turístico. O correto é BON-JOUR curto e sem ênfase no R." },
        { q: "Bonjour é sempre acentuado no JOUR?", a: "Em francês, o acento tônico da PALAVRA cai na última sílaba (JOUR), mas o acento é MUITO mais fraco que em português. As duas sílabas têm quase a mesma duração — só a última é levemente mais longa." },
      ],
      en: [
        { q: "How do I produce the BON nasal vowel?", a: "Trick: say « bo » while lightly pinching your nose. Release — you'll feel the air escaping. That nasal passage is what English/Portuguese speakers lack for /ɔ̃/." },
        { q: "Is the final R mandatory?", a: "Yes, but short. A brief uvular R, almost swallowed. Never stretch it: « BON-JOOOUR » sounds touristy. Keep it short with no R emphasis." },
        { q: "Where's the stress in « Bonjour »?", a: "In French, word stress falls on the LAST syllable (JOUR), but it's much weaker than in English. Both syllables are almost equal — the last is only slightly longer." },
      ],
      fr: [
        { q: "Comment faire la voyelle nasale BON ?", a: "Astuce : dis « bo » en pinçant légèrement le nez, puis relâche — tu sens l'air passer par le nez." },
        { q: "Le R final est-il obligatoire ?", a: "Oui, mais bref. Un R uvulaire court, presque avalé. Jamais étiré." },
        { q: "Où est l'accent tonique ?", a: "Sur la dernière syllabe (JOUR), mais très léger. Les deux syllabes sont presque égales." },
      ],
    },
  },
  {
    slug: "croissant",
    updated: "2026-02-24",
    word: "Croissant",
    ipa: "/kʁwa.sɑ̃/",
    meaning: { pt: "croissant (o pão)", en: "croissant (the pastry)", fr: "viennoiserie" },
    steps: {
      pt: [
        "CROI — não é « croi » com « ói » brasileiro. É /kʁwa/: um K raspado + R uvular + som « uá ». Tipo « kruá » com R francês.",
        "SSANT — /sɑ̃/: som de « sã » nasal (parecido com « são » brasileiro, mas sem o N). O A é ABERTO e nasal.",
        "Nada de « croissANT » com T final. O T é MUDO. Termina em « sã » nasal, ponto.",
      ],
      en: [
        "CROI — not « croy ». It's /kʁwa/: rough K + uvular R + « wa » sound. Like « krwa » with a French R.",
        "SSANT — /sɑ̃/: nasal « sah » sound, similar to a nasalised « sun » without the N.",
        "The final T is SILENT. Ends on a nasal « sah », no T at all.",
      ],
      fr: [
        "CROI — /kʁwa/, R uvulaire + « wa ».",
        "SSANT — /sɑ̃/, voyelle nasale ouverte.",
        "Le T final est muet.",
      ],
    },
    faq: {
      pt: [
        { q: "Por que o T final é mudo?", a: "Em francês, quase todas as consoantes finais (T, D, S, P, X) são mudas. Regra prática: pense « CaReFuL » — só C, R, F, L costumam ser pronunciados no fim. As outras, mudas." },
        { q: "Croissant tem gênero em francês?", a: "Masculino: UN croissant (não « une »). Mesma coisa que pain, café, thé — todos masculinos no vocabulário de padaria." },
      ],
      en: [
        { q: "Why is the final T silent?", a: "In French, most final consonants (T, D, S, P, X) are silent. Rule: only C, R, F, L (« CaReFuL ») tend to be pronounced. The rest, silent." },
        { q: "What's the gender of croissant?", a: "Masculine: UN croissant. Like pain, café, thé — all masculine in bakery vocabulary." },
      ],
      fr: [
        { q: "Pourquoi le T final est-il muet ?", a: "En français, la plupart des consonnes finales sont muettes. Seules C, R, F, L tendent à se prononcer." },
        { q: "Quel est le genre de « croissant » ?", a: "Masculin : UN croissant." },
      ],
    },
  },
  {
    slug: "paris",
    updated: "2026-02-24",
    word: "Paris",
    ipa: "/pa.ʁi/",
    meaning: { pt: "Paris (a cidade)", en: "Paris (the city)", fr: "capitale de la France" },
    steps: {
      pt: [
        "PA — /pa/ curto, com o A aberto (como em português « pá »).",
        "RI — /ʁi/: R UVULAR seguido de I curto e fechado. Nada de brasileiro « rrí ».",
        "O S FINAL É MUDO. Nunca « Pá-riss ». Diga « pa-RRI » com R francês. Simples.",
      ],
      en: [
        "PA — short /pa/ with open A.",
        "RI — /ʁi/: UVULAR R + short closed I. Not the English « ree ».",
        "THE FINAL S IS SILENT. Never « Pa-riss ». Say « pa-RRI » with French R.",
      ],
      fr: [
        "PA — /pa/ court.",
        "RI — /ʁi/, R uvulaire.",
        "S final muet.",
      ],
    },
    faq: {
      pt: [
        { q: "Por que muitos brasileiros dizem « Pá-riss »?", a: "Porque o inglês pronuncia o S. No francês, o S final é MUDO. Também estranha o R: é uvular, não brasileiro. « Pa-RRI » (com R francês) é a versão correta." },
        { q: "Como pronunciar « je viens de Paris » (« sou de Paris »)?", a: "/ʒə vjɛ̃ də pa.ʁi/. A ligação entre « de » e « Paris » é sutil: quase « d-pa-RRI »." },
      ],
      en: [
        { q: "Why do many English speakers say « Pa-riss »?", a: "Because English pronounces the S. In French, the final S is silent. « pa-RRI » with a French R is correct." },
        { q: "How to say « je viens de Paris »?", a: "/ʒə vjɛ̃ də pa.ʁi/ — the « de Paris » glides into « d-pa-RRI »." },
      ],
      fr: [
        { q: "Pourquoi certains disent « Pa-riss » ?", a: "Influence anglaise. Le S est muet en français." },
        { q: "Comment prononcer « je viens de Paris » ?", a: "/ʒə vjɛ̃ də pa.ʁi/, avec liaison douce." },
      ],
    },
  },
  {
    slug: "oui",
    updated: "2026-02-24",
    word: "Oui",
    ipa: "/wi/",
    meaning: { pt: "sim", en: "yes", fr: "affirmation" },
    steps: {
      pt: [
        "É UMA sílaba, não duas. /wi/ = « uí » colado. Como o som que o brasileiro faz pra brincar de « uiii » de arrepio, mas curto e neutro.",
        "Nunca « ou-i » separado. É /wi/ instantâneo. Semi-vogal W + vogal I, sem pausa.",
        "Tom neutro descendente no fim. Nada de subir o « i » como pergunta (a menos que seja uma).",
      ],
      en: [
        "It's ONE syllable, not two. /wi/ = « wee » with a very short W. Like the English « we ».",
        "Never split « o-ui ». Instant /wi/. Semi-vowel W + vowel I, no pause.",
        "Neutral falling tone at the end. Don't rise like a question (unless it IS one).",
      ],
      fr: [
        "Une seule syllabe : /wi/.",
        "Semi-voyelle W + I, sans pause.",
        "Ton neutre descendant.",
      ],
    },
    faq: {
      pt: [
        { q: "Ouais ou oui? Qual usar?", a: "« Oui » = neutro, sempre correto. « Ouais » = super casual (« yeah »), só com amigos. Nunca em contexto formal — soa preguiçoso." },
        { q: "Como dizer « sim, com certeza »?", a: "« Oui, bien sûr », « Oui, absolument », « Tout à fait ». « Ouais carrément » entre amigos." },
      ],
      en: [
        { q: "Ouais or oui? Which to use?", a: "« Oui » = neutral, always correct. « Ouais » = super casual (« yeah »), friends only. Never formal — sounds lazy." },
        { q: "How to say « yes, absolutely »?", a: "« Oui, bien sûr », « Oui, absolument », « Tout à fait »." },
      ],
      fr: [
        { q: "Ouais ou oui ?", a: "« Oui » neutre, « Ouais » familier." },
        { q: "Comment dire « oui absolument » ?", a: "« Tout à fait », « Oui, bien sûr », « Absolument »." },
      ],
    },
  },
  {
    slug: "je-ne-sais-pas",
    updated: "2026-02-24",
    word: "Je ne sais pas",
    ipa: "/ʒə nə sɛ pa/",
    meaning: { pt: "eu não sei", en: "I don't know", fr: "expression de doute" },
    steps: {
      pt: [
        "JE — /ʒə/: J francês (como « já » PT) + E fraco (« schwa », o E de « le »).",
        "NE SAIS — /nə sɛ/: NE quase mudo, SAIS com E aberto e S final MUDO. Nunca « saiss ».",
        "PAS — /pa/: PA curto, sem S. Igual « pá » em português.",
        "Rápido: /ʃə.pa/ na fala oral (« chpa »). Franceses cortam « je ne » e « sais » na fala corrida.",
      ],
      en: [
        "JE — /ʒə/: French J + weak schwa E.",
        "NE SAIS — /nə sɛ/: almost silent NE, SAIS with open E, silent final S.",
        "PAS — /pa/: short PA, no S.",
        "Fast speech: /ʃə.pa/ (« shpa »). Native speakers drop « je ne » and « sais » in casual speech.",
      ],
      fr: [
        "JE — /ʒə/ avec e faible.",
        "NE SAIS — /nə sɛ/, S muet.",
        "PAS — /pa/, S muet.",
        "À l'oral : /ʃə.pa/.",
      ],
    },
    faq: {
      pt: [
        { q: "Por que os franceses cortam a negação?", a: "Na fala rápida, « je ne sais pas » vira « chais pas » (/ʃɛ pa/) ou « chsépa ». O NE cai. É universal no francês oral moderno. Formal e escrito: sempre com NE." },
        { q: "Como responder « je ne sais pas » educadamente se te perguntarem?", a: "« Désolé(e), je ne sais pas » — adiciona « désolé » (desculpa) pra suavizar. « Aucune idée » (« nenhuma ideia ») é uma alternativa curta." },
      ],
      en: [
        { q: "Why do French people drop « ne »?", a: "In fast speech, « je ne sais pas » becomes « chais pas » or « chsépa ». The NE drops. Universal in modern oral French. Formal/written: keep NE." },
        { q: "How to reply politely?", a: "« Désolé(e), je ne sais pas » softens it. Or « Aucune idée » (« no idea »)." },
      ],
      fr: [
        { q: "Pourquoi laisser tomber « ne » ?", a: "Oral courant : « je ne sais pas » → « chais pas ». Standard depuis longtemps." },
        { q: "Comment répondre poliment ?", a: "« Désolé(e), je ne sais pas » ou « Aucune idée »." },
      ],
    },
  },
  {
    slug: "rendez-vous",
    updated: "2026-02-24",
    word: "Rendez-vous",
    ipa: "/ʁɑ̃.de.vu/",
    meaning: { pt: "encontro / consulta / compromisso", en: "appointment / meeting / date", fr: "réunion planifiée" },
    steps: {
      pt: [
        "REN — /ʁɑ̃/: R uvular + vogal nasal aberta (não confunda com /ɔ̃/ de BON!). É como « rã » brasileiro mas MAIS ABERTO e nasal.",
        "DEZ — /de/: D suave + E fechado (como português « dê »). O Z final é MUDO.",
        "VOUS — /vu/: V + OU fechado (não « vôu »). Termina em U francês fechado.",
        "Cadência: /ʁɑ̃.de.vu/, três sílabas iguais, sem hifens sonoros.",
      ],
      en: [
        "REN — /ʁɑ̃/: uvular R + open nasal vowel (different from /ɔ̃/ in BON!).",
        "DEZ — /de/: soft D + closed E. The final Z is SILENT.",
        "VOUS — /vu/: V + closed OU (not « voo »). Ends with French closed U.",
        "Rhythm: /ʁɑ̃.de.vu/, three equal syllables. The hyphens are spelling, not pauses.",
      ],
      fr: [
        "REN — /ʁɑ̃/, voyelle nasale ouverte.",
        "DEZ — /de/, Z muet.",
        "VOUS — /vu/, U fermé.",
      ],
    },
    faq: {
      pt: [
        { q: "Rendez-vous é sempre romântico?", a: "NÃO em francês! É QUALQUER compromisso agendado: consulta médica (rendez-vous médical), reunião de trabalho, encontro amoroso. Só o contexto define. Em inglês virou romântico por importação estilizada." },
        { q: "Como marcar um rendez-vous ao telefone?", a: "« Bonjour, je voudrais prendre un rendez-vous, s'il vous plaît » (« Bom dia, eu gostaria de marcar uma consulta »). Vai pedir data (« quelle date ? ») e horário (« à quelle heure ? »)." },
      ],
      en: [
        { q: "Is rendez-vous always romantic?", a: "NOT in French! It means ANY scheduled appointment: doctor (rendez-vous médical), work meeting, romantic date. Context defines it. English stylised it into romance." },
        { q: "How to book a rendez-vous by phone?", a: "« Bonjour, je voudrais prendre un rendez-vous, s'il vous plaît ». They'll ask for date (« quelle date ? ») and time (« à quelle heure ? »)." },
      ],
      fr: [
        { q: "Rendez-vous est-il romantique ?", a: "Pas nécessairement. Médical, travail, amoureux — le contexte décide." },
        { q: "Comment prendre rendez-vous par téléphone ?", a: "« Bonjour, je voudrais prendre un rendez-vous, s'il vous plaît »." },
      ],
    },
  },
  {
    slug: "chocolat",
    updated: "2026-02-24",
    word: "Chocolat",
    ipa: "/ʃɔ.kɔ.la/",
    meaning: { pt: "chocolate", en: "chocolate", fr: "confiserie sucrée" },
    steps: {
      pt: [
        "CHO — /ʃɔ/: som « ch » como no português « chá ». O O é aberto, curto.",
        "CO — /kɔ/: K + O aberto. Sílaba curta, sem prolongar.",
        "LAT — /la/: L + A aberto. O T FINAL É MUDO. Nunca diga « cho-co-latt ».",
        "Três sílabas iguais e rápidas: /ʃɔ.kɔ.la/.",
      ],
      en: [
        "CHO — /ʃɔ/: « sh » sound + open O. Short syllable.",
        "CO — /kɔ/: K + open O.",
        "LAT — /la/: L + open A. Final T is SILENT. Never « cho-co-latt ».",
        "Three equal quick syllables: /ʃɔ.kɔ.la/.",
      ],
      fr: [
        "CHO — /ʃɔ/, O ouvert.",
        "CO — /kɔ/, syllabe brève.",
        "LAT — /la/, T final muet.",
      ],
    },
    faq: {
      pt: [
        { q: "Chocolat quente é « chocolat chaud »?", a: "Sim: « un chocolat chaud, s'il vous plaît ». Nas boulangeries francesas é obrigatório no inverno, servido em xícara larga. Se quiser gelado: « un chocolat froid » (raro, prefira « un lait chocolaté »)." },
      ],
      en: [
        { q: "Hot chocolate is « chocolat chaud »?", a: "Yes: « un chocolat chaud, s'il vous plaît ». Mandatory in French bakeries in winter, served in a wide cup. Cold version: rare — prefer « un lait chocolaté »." },
      ],
      fr: [
        { q: "Chocolat chaud ou froid ?", a: "« Un chocolat chaud » en hiver. Le froid est rare, on dit plutôt « lait chocolaté »." },
      ],
    },
  },
  {
    slug: "sil-vous-plait",
    updated: "2026-02-24",
    word: "S'il vous plaît",
    ipa: "/sil vu plɛ/",
    meaning: { pt: "por favor", en: "please", fr: "formule de politesse" },
    steps: {
      pt: [
        "SIL — /sil/: S + I curto + L. Sem pausa depois do apóstrofo.",
        "VOUS — /vu/: V + OU fechado. NÃO « vôu ».",
        "PLAÎT — /plɛ/: PL + E ABERTO (como português « pé »). O T FINAL É MUDO.",
        "Fala rápida, colada: /sil.vu.plɛ/. Não é « sil-vu-plai-TE ».",
      ],
      en: [
        "SIL — /sil/: S + short I + L. No pause after the apostrophe.",
        "VOUS — /vu/: V + closed OU. Not « voo ».",
        "PLAÎT — /plɛ/: PL + OPEN E (like English « pet »). Final T is SILENT.",
        "Fast, blended: /sil.vu.plɛ/. Never « sil-vu-plate ».",
      ],
      fr: [
        "SIL — /sil/, I bref.",
        "VOUS — /vu/, U fermé.",
        "PLAÎT — /plɛ/, T muet.",
      ],
    },
    faq: {
      pt: [
        { q: "Sempre no fim da frase?", a: "Sim, na maioria dos casos: « L'addition, s'il vous plaît ». Pode vir no INÍCIO em pedido enfático: « S'il vous plaît, écoutez-moi ». Nunca no meio da frase — soa forçado." },
        { q: "Existe alguma versão abreviada?", a: "SVP em escrita informal (SMS, notas). Nunca dito em voz alta. Formal escrito: « s'il vous plaît » por extenso." },
      ],
      en: [
        { q: "Always at end of sentence?", a: "Yes, mostly: « L'addition, s'il vous plaît ». Can go at BEGINNING for emphasis: « S'il vous plaît, écoutez-moi ». Never in the middle — sounds forced." },
        { q: "Any short version?", a: "SVP in informal writing (SMS, notes). Never spoken aloud. Formal writing: spell it out." },
      ],
      fr: [
        { q: "En début ou fin de phrase ?", a: "Fin le plus souvent. En début pour insister." },
        { q: "Abréviation ?", a: "SVP à l'écrit informel uniquement." },
      ],
    },
  },
  { slug: "eiffel", updated: "2026-02-24", word: "Tour Eiffel", ipa: "/tuʁ ɛ.fɛl/",
    meaning: { pt: "Torre Eiffel", en: "Eiffel Tower", fr: "monument parisien" },
    steps: {
      pt: ["TOUR — /tuʁ/: T + OU fechado + R uvular. NÃO « tour » inglês.", "EI — /ɛ/: E ABERTO curto. Nada de « aí ».", "FFEL — /fɛl/: F + E aberto + L. NÃO « ei-FÊU »."],
      en: ["TOUR — /tuʁ/: T + closed OU + uvular R.", "EI — /ɛ/: open E, short.", "FFEL — /fɛl/: F + open E + L."],
      fr: ["Tour /tuʁ/, Eiffel /ɛ.fɛl/. Voyelle « è » ouverte, pas « é »."],
    },
    faq: {
      pt: [ { q: "Por que « Tour Eiffel » e não « Tour d'Eiffel »?", a: "Convenção histórica em francês: o nome do arquiteto vira epíteto direto, sem preposição. Assim como « Rue Balzac », « Place Vendôme » — o nome cola direto no substantivo. « Tour d'Eiffel » é incorreto." } ],
      en: [ { q: "Why « Tour Eiffel » not « Tour d'Eiffel »?", a: "Historical French convention: the architect's name becomes a direct epithet, no preposition. Like « Rue Balzac », « Place Vendôme ». « Tour d'Eiffel » is wrong." } ],
      fr: [ { q: "Pourquoi « Tour Eiffel » sans « d' » ?", a: "Convention : nom d'architecte en épithète directe." } ],
    },
  },
  { slug: "champs-elysees", updated: "2026-02-24", word: "Champs-Élysées", ipa: "/ʃɑ̃ ze.li.ze/",
    meaning: { pt: "Champs-Élysées", en: "Champs-Élysées", fr: "avenue parisienne" },
    steps: {
      pt: ["CHAMPS — /ʃɑ̃/: som CH + vogal nasal aberta. O S final é MUDO.", "É — /e/: E fechado curto (como português « ê »).", "LY — /li/: L + I curto.", "SÉES — /ze/: liga com Élysées via LIAISON. O S de « Champs » se liga foneticamente ao Élysées, virando /Z/.", "Junto: /ʃɑ̃.ze.li.ze/, 4 sílabas rápidas."],
      en: ["CHAMPS — /ʃɑ̃/: SH + open nasal vowel. Silent final S.", "É — /e/: closed short E.", "LY — /li/.", "SÉES — /ze/: LIAISON with Élysées — the silent S of Champs becomes /z/.", "Blended: /ʃɑ̃.ze.li.ze/, 4 quick syllables."],
      fr: ["Champs /ʃɑ̃/, liaison en /z/, Élysées /e.li.ze/."],
    },
    faq: {
      pt: [ { q: "O que é « liaison » aqui?", a: "É a lei fonética francesa: consoante final MUDA (S de Champs) se torna sonora quando a próxima palavra começa com VOGAL. « Champs Élysées » vira « champ-ZÉ-lisé ». Aprender liaison = pular 1 nível na pronúncia." } ],
      en: [ { q: "What's the liaison here?", a: "French phonetic rule: silent final consonant (S in Champs) becomes voiced when the next word starts with a VOWEL. « Champs Élysées » becomes « shamp-ZAY-lee-zay ». Master liaison = jump one level." } ],
      fr: [ { q: "Qu'est-ce que la liaison ?", a: "Le S muet de « Champs » se prononce en /z/ devant voyelle." } ],
    },
  },
  { slug: "baguette", updated: "2026-02-24", word: "Baguette", ipa: "/ba.ɡɛt/",
    meaning: { pt: "baguete", en: "baguette", fr: "pain long" },
    steps: {
      pt: ["BA — /ba/: B + A aberto.", "GU — /ɡ/: G duro (como português « gato »), NÃO como « gente ».", "ETTE — /ɛt/: E ABERTO + T final SONORO (raro: aqui o T se ouve por causa do E aberto que o antecede)."],
      en: ["BA — /ba/.", "GU — /ɡ/: hard G, not soft.", "ETTE — /ɛt/: open E + audible T (rare: T sounds here because of the preceding open E)."],
      fr: ["Ba /ba/, guette /ɡɛt/. T final audible."],
    },
    faq: {
      pt: [ { q: "Por que aqui o T final SOA?", a: "Regra sutil: quando a última sílaba tem E aberto (« ette », « erre », « esse ») seguido de consoante, a consoante SOA. Exemplo: baguette /ba.gɛT/, chette /ʃɛT/. Diferente de « croissant » onde o T é mudo." } ],
      en: [ { q: "Why is the final T audible here?", a: "Subtle rule: when the last syllable has open E (« ette », « erre », « esse ») followed by a consonant, the consonant SOUNDS. Different from « croissant » where T is silent." } ],
      fr: [ { q: "Pourquoi le T sonore ?", a: "En finale -ette, le T se prononce." } ],
    },
  },
  { slug: "amour", updated: "2026-02-24", word: "Amour", ipa: "/a.muʁ/",
    meaning: { pt: "amor", en: "love", fr: "sentiment amoureux" },
    steps: {
      pt: ["A — /a/: A aberto curto.", "MOUR — /muʁ/: M + OU fechado + R uvular. NÃO « mour » inglês."],
      en: ["A — /a/: open short A.", "MOUR — /muʁ/: M + closed OU + uvular R."],
      fr: ["A /a/, mour /muʁ/. R uvulaire final."],
    },
    faq: {
      pt: [ { q: "« Amour » é masculino ou feminino?", a: "MASCULINO no singular: « un grand amour ». Curiosamente, no PLURAL vira feminino em literatura clássica: « des amours passionnées ». Na fala moderna, mantém-se masculino em ambos. Exceção histórica que só aparece em poesia." } ],
      en: [ { q: "Is « amour » masculine or feminine?", a: "MASCULINE singular: « un grand amour ». Curiously, in the PLURAL it becomes feminine in classical literature: « des amours passionnées ». Modern speech keeps it masculine in both. Historical quirk from poetry." } ],
      fr: [ { q: "Genre d'« amour » ?", a: "Masculin au singulier, féminin au pluriel en littérature classique." } ],
    },
  },
  { slug: "montmartre", updated: "2026-02-24", word: "Montmartre", ipa: "/mɔ̃.maʁtʁ/",
    meaning: { pt: "Montmartre (bairro de Paris)", en: "Montmartre (Paris neighbourhood)", fr: "quartier parisien" },
    steps: {
      pt: ["MONT — /mɔ̃/: M + vogal nasal ON. T final MUDO.", "MARTRE — /maʁtʁ/: M + A + R uvular + T + R uvular final. Dois R's uvulares seguidos = clássico teste de sotaque."],
      en: ["MONT — /mɔ̃/: M + nasal ON. Silent final T.", "MARTRE — /maʁtʁ/: two uvular Rs in a row — classic accent test."],
      fr: ["Mont /mɔ̃/, martre /maʁtʁ/. Deux R uvulaires."],
    },
    faq: {
      pt: [ { q: "Como treinar dois R uvulares seguidos?", a: "Comece devagar: MAR-trê (pausa breve entre os R). Depois acelere: MAR-TR (colados). Alvo: fluidez sem forçar. É o teste-cabeça-de-brasileiro por excelência — se domina, ninguém identifica que você aprendeu francês depois de adulto." } ],
      en: [ { q: "How to drill two uvular Rs?", a: "Start slow: MAR-tr (short pause between Rs). Then speed up: MAR-TR (blended). Aim for fluidity without forcing. This is THE test for non-native speakers." } ],
      fr: [ { q: "Comment enchaîner deux R uvulaires ?", a: "Doucement d'abord, puis lié." } ],
    },
  },
  { slug: "euro", updated: "2026-02-24", word: "Euro", ipa: "/ø.ʁo/",
    meaning: { pt: "euro (moeda)", en: "euro (currency)", fr: "monnaie européenne" },
    steps: {
      pt: ["EU — /ø/: vogal francesa característica, boca em O mas som de E. Semelhante ao « eu » de « feu ». Impossível em português brasileiro sem treino.", "RO — /ʁo/: R uvular + O fechado."],
      en: ["EU — /ø/: characteristic French vowel, mouth shaped as O but sound of E. Impossible for English without training.", "RO — /ʁo/: uvular R + closed O."],
      fr: ["Eu /ø/, ro /ʁo/. Voyelle antérieure arrondie."],
    },
    faq: {
      pt: [ { q: "Como fazer o som /ø/ do « EU »?", a: "Truque: forme os lábios como se fosse dizer O, mas produza o som de Ê. Boca redonda, língua adiante. Se sai « ó », a língua está errada. Pratique em par: « feu » (fogo), « peu » (pouco), « bleu » (azul)." } ],
      en: [ { q: "How to make the /ø/ sound of « EU »?", a: "Trick: shape your lips as if saying O, but produce the sound of Ê. Round mouth, tongue forward. If it sounds like « oh », your tongue is wrong. Drill pairs: « feu », « peu », « bleu »." } ],
      fr: [ { q: "Comment faire le /ø/ ?", a: "Lèvres arrondies comme pour O, son de É." } ],
    },
  },
  { slug: "au-revoir", updated: "2026-02-19", word: "Au revoir", ipa: "/o ʁə.vwaʁ/",
    meaning: { pt: "até logo / tchau (neutro-formal)", en: "goodbye (neutral-formal)", fr: "salutation de départ standard" },
    steps: {
      pt: [
        "AU — /o/: um O fechado, sem ditongo. Não é « au » de português « saudade »; é apenas O puro e curto.",
        "RE — /ʁə/: R uvular seguido de e mudo (« schwa »). Praticamente engolido, quase imperceptível.",
        "VOIR — /vwaʁ/: V-W-A-R. O A é aberto e o R final é o famoso R francês de garganta.",
      ],
      en: [
        "AU — /o/: closed O, no diphthong. Just a pure, short O.",
        "RE — /ʁə/: uvular R + schwa. Barely pronounced, almost swallowed.",
        "VOIR — /vwaʁ/: V-W-A-R. Open A, final uvular R.",
      ],
      fr: ["Au /o/, re /ʁə/ à peine prononcé, voir /vwaʁ/ avec R uvulaire final."],
    },
    faq: {
      pt: [
        { q: "É « ô-revoar » ou « ô-revuár »?", a: "Nem um nem outro. É « ô-rvuár » (o « re » quase some). O RE é um schwa engolido: /ʁə/. Se você exagera o E, soa turístico. Escute qualquer francês: soa como « orvuár », duas sílabas rápidas, não três." },
        { q: "Quando usar « au revoir » vs « salut »?", a: "AU REVOIR é neutro e sempre seguro (loja, restaurante, colega). SALUT é informal — só entre amigos ou família. Nunca diga « salut » ao vendedor: soa infantil. Regra prática: quando em dúvida, sempre « au revoir »." },
        { q: "E « à bientôt » é a mesma coisa?", a: "Não. À BIENTÔT (« até em breve ») implica que você vai rever a pessoa em breve. AU REVOIR não implica reencontro. Um garçom fala « au revoir »; um amigo com quem você marcou pra semana que vem, « à bientôt »." },
      ],
      en: [
        { q: "Is it « oh-re-vwar » or « oh-re-voir »?", a: "Neither. It's « or-vwar » — the RE almost vanishes (schwa /ə/). Sounds like two fast syllables. Overpronouncing the E marks you as a tourist." },
        { q: "When to use « au revoir » vs « salut »?", a: "AU REVOIR is neutral and always safe. SALUT is casual — friends and family only. Never say « salut » to a shopkeeper." },
        { q: "Is « à bientôt » the same?", a: "No. À BIENTÔT (« see you soon ») implies you'll meet again shortly. AU REVOIR doesn't imply that." },
      ],
      fr: [
        { q: "« Au revoir » ou « salut » ?", a: "Au revoir : neutre. Salut : familier." },
        { q: "« À bientôt » vs « au revoir » ?", a: "À bientôt implique un revoir proche." },
      ],
    },
  },
];

// ------------------------------------------------------------------
// "Diferença entre X e Y em francês" — 4 entries
// ------------------------------------------------------------------
export const DIFFERENCES = [
  {
    slug: "tu-vs-vous",
    updated: "2026-02-24",
    a: "Tu",
    b: "Vous",
    intro: {
      pt: "TU é a forma íntima (você-próximo). VOUS é a forma formal e/ou plural. Escolher errado é o erro social nº 1 de brasileiros na França.",
      en: "TU is the intimate « you » (close). VOUS is formal and/or plural. Getting this wrong is the #1 social mistake Brazilians make in France.",
      fr: "TU est la forme intime, VOUS la forme formelle et/ou plurielle.",
    },
    rules: {
      pt: [
        "TU: amigos, família, crianças, colegas próximos, animais.",
        "VOUS: qualquer desconhecido acima de 12 anos, professores, chefes, garçons, funcionários, atendentes, no telefone com empresas.",
        "Regra de ouro: comece SEMPRE com VOUS. A outra pessoa te oferece o TU (« On peut se tutoyer ? ») — só aí você troca.",
        "Nunca ofereça o TU pra alguém mais velho ou hierarquicamente superior. Espere que ele ofereça.",
      ],
      en: [
        "TU: friends, family, kids, close colleagues, pets.",
        "VOUS: any stranger over 12, teachers, bosses, waiters, staff, on phone with companies.",
        "Golden rule: ALWAYS start with VOUS. Let the other person offer TU (« On peut se tutoyer ? ») — only then switch.",
        "Never offer TU to someone older or higher-ranking. Wait for them.",
      ],
      fr: [
        "TU : amis, famille, enfants, collègues proches, animaux.",
        "VOUS : tout inconnu > 12 ans, professeurs, chefs, serveurs, employés, entreprises au téléphone.",
        "Règle d'or : commence toujours par VOUS. Attends qu'on te propose le TU.",
        "N'offre jamais le TU à plus âgé ou hiérarchiquement supérieur.",
      ],
    },
    faq: {
      pt: [
        { q: "Um garçom da minha idade — TU ou VOUS?", a: "VOUS, sempre. Idade parecida não importa: relação profissional cliente-atendente exige VOUS. Só se trocarem TU explicitamente entre si em contexto informal (bar noturno com amigos)." },
        { q: "E se o francês me tratar por TU logo de cara?", a: "Ele te oferece intimidade. Você pode responder com TU. Mas SÓ se sentir que é genuíno (não turista sendo desprezado). No dúvida, mantenha VOUS até o gelo quebrar." },
        { q: "TU com criança pequena está OK?", a: "Sim, sempre TU com crianças até ~15 anos. Adolescente 15+, ainda TU se você é o adulto responsável ou parente; VOUS se você é professor ou funcionário." },
      ],
      en: [
        { q: "A waiter my age — TU or VOUS?", a: "VOUS, always. Similar age doesn't matter: professional customer-staff relationship requires VOUS. Only in casual settings (bar with friends) does it flex." },
        { q: "What if a French person uses TU with me right away?", a: "They're offering intimacy. You can respond with TU. But only if it feels genuine. When in doubt, stick to VOUS until the ice breaks." },
        { q: "TU with young kids?", a: "Yes, always TU with kids up to ~15. Teens 15+: still TU if you're the responsible adult or relative; VOUS if you're a teacher or staff." },
      ],
      fr: [
        { q: "Un serveur de mon âge — TU ou VOUS ?", a: "VOUS, toujours. Relation professionnelle exige VOUS." },
        { q: "Si un Français me tutoie d'emblée ?", a: "Il t'offre l'intimité. Tu peux tutoyer en retour, si le contexte est sincère." },
        { q: "TU avec les enfants ?", a: "Oui, jusqu'à ~15 ans. Après, dépend du contexte." },
      ],
    },
  },
  {
    slug: "savoir-vs-connaitre",
    updated: "2026-02-24",
    a: "Savoir",
    b: "Connaître",
    intro: {
      pt: "Ambos traduzem « saber » em português, mas em francês são radicalmente diferentes. SAVOIR = fato / habilidade. CONNAÎTRE = ter familiaridade com pessoa, lugar, obra.",
      en: "Both translate as « to know », but they're radically different. SAVOIR = a fact or skill. CONNAÎTRE = to be familiar with a person, place, work.",
      fr: "SAVOIR : fait, capacité. CONNAÎTRE : familiarité avec personne, lieu, œuvre.",
    },
    rules: {
      pt: [
        "SAVOIR: seguido de fato / info / infinitivo. « Je sais où c'est » (sei onde é). « Je sais nager » (sei nadar).",
        "CONNAÎTRE: seguido de OBJETO DIRETO (pessoa, lugar, coisa). « Je connais Paris ». « Je connais Marie ». « Je connais cette chanson ».",
        "Teste rápido: se cabe « estou familiarizado com... », use CONNAÎTRE. Se cabe « eu sei fazer / sei que... », use SAVOIR.",
      ],
      en: [
        "SAVOIR: followed by a fact / info / infinitive. « Je sais où c'est » (I know where it is). « Je sais nager » (I can swim).",
        "CONNAÎTRE: followed by a DIRECT OBJECT (person, place, thing). « Je connais Paris ». « Je connais Marie ».",
        "Quick test: if « I'm familiar with... » works, use CONNAÎTRE. If « I know that / know how to... » works, use SAVOIR.",
      ],
      fr: [
        "SAVOIR : fait, info, infinitif.",
        "CONNAÎTRE : objet direct (personne, lieu, chose).",
        "Test : « être familier avec » → CONNAÎTRE. « savoir que / savoir faire » → SAVOIR.",
      ],
    },
    faq: {
      pt: [
        { q: "« Eu conheço a resposta » — savoir ou connaître?", a: "SAVOIR: « Je sais la réponse ». A resposta é um fato, não uma familiaridade cultivada. Regra: se o objeto é INFORMAÇÃO, é sempre SAVOIR." },
        { q: "« Eu conheço Paris » — savoir ou connaître?", a: "CONNAÎTRE: « Je connais Paris ». Paris é um lugar do qual você tem familiaridade vivenciada. Sempre CONNAÎTRE com nomes próprios de lugar/pessoa/obra." },
        { q: "Existe algum caso em que os dois funcionam?", a: "Raro. « Je sais / connais cette chanson » — ambos existem mas SIGNIFICAM COISAS DIFERENTES: « je sais » = sei cantar / sei a letra. « je connais » = já ouvi antes, é familiar." },
      ],
      en: [
        { q: "« I know the answer » — savoir or connaître?", a: "SAVOIR: « Je sais la réponse ». The answer is a fact. Info = always SAVOIR." },
        { q: "« I know Paris » — savoir or connaître?", a: "CONNAÎTRE: « Je connais Paris ». Paris is a place you're familiar with. Always CONNAÎTRE with proper names of place/person/work." },
        { q: "Any case where both work?", a: "Rare. « Je sais / connais cette chanson » — both exist but MEAN DIFFERENT THINGS: « je sais » = I can sing it / know the lyrics. « je connais » = I've heard it, it's familiar." },
      ],
      fr: [
        { q: "« Je connais la réponse » ou « je sais la réponse » ?", a: "SAVOIR — c'est une information/un fait." },
        { q: "« Je connais Paris » ou « je sais Paris » ?", a: "CONNAÎTRE — lieu/personne/œuvre." },
        { q: "Les deux peuvent-ils s'utiliser ?", a: "Rarement — sens différent : « je sais la chanson » = je sais la chanter. « je connais la chanson » = je l'ai déjà entendue." },
      ],
    },
  },
  {
    slug: "an-vs-annee",
    updated: "2026-02-24",
    a: "An",
    b: "Année",
    intro: {
      pt: "Ambos significam « ano », mas o uso é bem diferente. AN = ano como unidade contável, pontual. ANNÉE = ano como duração / conteúdo / experiência.",
      en: "Both mean « year », but usage differs. AN = year as a countable, punctual unit. ANNÉE = year as duration / content / experience.",
      fr: "AN : unité comptable, ponctuelle. ANNÉE : durée, contenu, expérience.",
    },
    rules: {
      pt: [
        "AN: com números (deux ans, cinq ans, dix ans). Idade: « J'ai 30 ans ». Contagem seca: « il y a 3 ans ».",
        "ANNÉE: quando descreve o CONTEÚDO do ano (« une bonne année »), a DURAÇÃO como experiência (« pendant l'année »), o ano civil (« l'année 2026 »).",
        "Truque: se pode substituir por « período de 365 dias vivido » → ANNÉE. Se é só « contagem » → AN.",
      ],
      en: [
        "AN: with numbers (deux ans, cinq ans, dix ans). Age: « J'ai 30 ans ». Bare count: « il y a 3 ans » (3 years ago).",
        "ANNÉE: when describing the CONTENT of the year (« une bonne année » — a good year), DURATION as experience (« pendant l'année »), the calendar year (« l'année 2026 »).",
        "Trick: if you can replace with « period lived » → ANNÉE. If just « count » → AN.",
      ],
      fr: [
        "AN : avec nombres (deux ans, cinq ans), âge, comptage.",
        "ANNÉE : contenu (« bonne année »), durée vécue, année civile.",
      ],
    },
    faq: {
      pt: [
        { q: "« Feliz ano novo »?", a: "« Bonne année ! » (não « Bon an ! »). Pra desejar bom conteúdo pro ano, é sempre ANNÉE. « Bonne année et bonne santé ! » — clássico." },
        { q: "« Tenho 30 anos »?", a: "« J'ai 30 ans » — sempre AN com idade. NUNCA « années » aqui. Idade é contagem seca de anos vividos, então AN." },
        { q: "« Este ano vou pra França »?", a: "« Cette année, je vais en France ». O ano como duração vivida = ANNÉE. « Cet an » não existe em francês moderno." },
      ],
      en: [
        { q: "How to say « Happy new year »?", a: "« Bonne année ! » (not « Bon an ! »). To wish good content for the year: always ANNÉE." },
        { q: "How to say « I'm 30 years old »?", a: "« J'ai 30 ans » — always AN with age. NEVER « années » here. Age = raw count = AN." },
        { q: "How to say « This year I'm going to France »?", a: "« Cette année, je vais en France ». Year as experienced duration = ANNÉE. « Cet an » doesn't exist in modern French." },
      ],
      fr: [
        { q: "Comment dire « Bonne année » ?", a: "« Bonne année ! » — souhait, donc ANNÉE." },
        { q: "« J'ai 30 ans » ou « années » ?", a: "AN — âge = comptage." },
        { q: "« Cette année/an je vais... » ?", a: "« Cette année » — durée vécue, ANNÉE." },
      ],
    },
  },
  {
    slug: "bien-vs-bon",
    updated: "2026-02-24",
    a: "Bien",
    b: "Bon",
    intro: {
      pt: "Ambos traduzem « bom / bem » em português. BON é ADJETIVO (qualifica um substantivo). BIEN é ADVÉRBIO (qualifica um verbo, adjetivo ou outro advérbio).",
      en: "Both translate as « good / well ». BON is an ADJECTIVE (qualifies a noun). BIEN is an ADVERB (qualifies a verb, adjective or another adverb).",
      fr: "BON : adjectif. BIEN : adverbe.",
    },
    rules: {
      pt: [
        "BON: antes ou depois de SUBSTANTIVO. « Un bon vin » (um bom vinho). « Un livre bon » (raro, quase sempre BON vem antes).",
        "BIEN: qualifica VERBO. « Je mange bien » (como bem). « Il chante bien » (ele canta bem). Nunca com substantivo direto.",
        "Truque: pense em inglês. GOOD = BON (« good wine »). WELL = BIEN (« I eat well »).",
      ],
      en: [
        "BON: before or after a NOUN. « Un bon vin » (a good wine). Position: nearly always BEFORE the noun in French.",
        "BIEN: qualifies a VERB. « Je mange bien » (I eat well). « Il chante bien » (he sings well). Never directly with a noun.",
        "Trick: GOOD = BON (« good wine »). WELL = BIEN (« I eat well »).",
      ],
      fr: [
        "BON : adjectif, qualifie un nom.",
        "BIEN : adverbe, qualifie un verbe / adjectif / autre adverbe.",
      ],
    },
    faq: {
      pt: [
        { q: "« Está bem » ou « está bom »?", a: "« Ça va bien » (bem — advérbio, qualifica o verbo ir/estar). « C'est bon » (bom — adjetivo, descreve algo concreto). São expressões diferentes." },
        { q: "« Muito bom » se traduz como?", a: "« Très bon » (adjetivo qualifica substantivo: « très bon vin »). « Très bien » (advérbio: « très bien fait » — muito bem feito, « très bien joué »)." },
        { q: "« Ele fala bem » é bon ou bien?", a: "BIEN. « Il parle bien » — advérbio qualificando verbo. « Il parle bon » está errado. Regra sem exceção: com verbo, use BIEN." },
      ],
      en: [
        { q: "« I'm doing well » or « It's good »?", a: "« Ça va bien » (well — adverb, qualifies the verb). « C'est bon » (good — adjective, describes something concrete)." },
        { q: "How to translate « very good »?", a: "« Très bon » (adjective + noun: « très bon vin »). « Très bien » (adverb: « très bien fait »)." },
        { q: "« He speaks well »?", a: "BIEN. « Il parle bien ». Verb → adverb → BIEN. No exception." },
      ],
      fr: [
        { q: "« Ça va bien » ou « ça va bon » ?", a: "« Ça va bien » — adverbe." },
        { q: "« Très bon » ou « très bien » ?", a: "« Très bon » avec nom, « très bien » avec verbe/adjectif." },
        { q: "« Il parle... » ?", a: "« Il parle bien » — adverbe avec verbe." },
      ],
    },
  },
  { slug: "cest-vs-il-est", updated: "2026-02-24", a: "C'est", b: "Il / Elle est",
    intro: {
      pt: "Ambos traduzem « é » em português, mas o uso é rígido em francês. C'EST + adjetivo geral ou substantivo com artigo. IL/ELLE EST + profissão, nacionalidade, adjetivo sobre pessoa específica sem artigo.",
      en: "Both translate as « it is / he is / she is », but usage is strict in French. C'EST + general adjective or noun with article. IL/ELLE EST + profession, nationality, adjective about a specific person without article.",
      fr: "C'EST + adjectif général / nom avec article. IL/ELLE EST + profession, nationalité, adjectif spécifique.",
    },
    rules: {
      pt: [
        "C'EST: apontando pra algo abstrato ou geral. « C'est bon » (comida). « C'est loin ». « C'est mon frère » (COM artigo « mon »).",
        "IL/ELLE EST: descrevendo pessoa/coisa específica SEM artigo. « Il est médecin » (SEM UN). « Elle est française » (SEM UNE). « Il est grand ».",
        "Truque: se poderia dizer « ISSO é... », use C'EST. Se poderia dizer « ele/ela é... » sem « um/uma », use IL/ELLE EST.",
      ],
      en: [
        "C'EST: pointing at something abstract or general. « C'est bon » (the food). « C'est loin ». « C'est mon frère » (WITH « mon »).",
        "IL/ELLE EST: describing a specific person/thing WITHOUT article. « Il est médecin » (NO UN). « Elle est française » (NO UNE).",
        "Trick: if you could say « THIS is... », use C'EST. If « he/she is... » without « a », use IL/ELLE EST.",
      ],
      fr: [
        "C'EST + adjectif général ou nom avec déterminant.",
        "IL/ELLE EST + attribut sans article (profession, nationalité).",
      ],
    },
    faq: {
      pt: [
        { q: "« Ele é médico » — c'est ou il est?", a: "IL EST médecin. Profissão SEM ARTIGO usa il est. « C'est un médecin » também funciona MAS muda o sentido (« é um médico » genérico, apontando)." },
        { q: "Como saber qual usar rapidamente?", a: "Regra do ARTIGO: se depois vem substantivo COM artigo (le, la, un, une, mon...), C'EST. Se substantivo SEM artigo ou adjetivo qualificando pessoa, IL/ELLE EST." },
      ],
      en: [
        { q: "« He's a doctor » — c'est or il est?", a: "IL EST médecin. Profession WITHOUT article uses il est. « C'est un médecin » also works BUT changes the meaning (« he's a doctor » generically, pointing)." },
        { q: "Quick rule?", a: "ARTICLE rule: if what follows is a noun WITH article, C'EST. If noun WITHOUT article or adjective about a person, IL/ELLE EST." },
      ],
      fr: [
        { q: "« Il est médecin » ou « c'est un médecin » ?", a: "Les deux existent, sens légèrement différent." },
        { q: "Règle rapide ?", a: "Article présent → c'est. Absent → il/elle est." },
      ],
    },
  },
  { slug: "depuis-vs-pendant", updated: "2026-02-24", a: "Depuis", b: "Pendant",
    intro: {
      pt: "Ambos indicam duração de tempo em português (« desde », « durante »). DEPUIS = ação começou no passado e CONTINUA agora. PENDANT = duração TOTAL de uma ação (passada, presente ou futura).",
      en: "Both indicate time duration. DEPUIS = action started in the past and CONTINUES now. PENDANT = TOTAL duration of an action (past, present or future).",
      fr: "DEPUIS : action commencée dans le passé et qui dure. PENDANT : durée totale d'une action.",
    },
    rules: {
      pt: [
        "DEPUIS + verbo no PRESENTE = ainda acontece. « Je vis à Paris depuis 3 ans » (moro AINDA).",
        "PENDANT + verbo em qualquer tempo = duração fechada. « J'ai vécu à Paris pendant 3 ans » (viveu, agora não).",
        "Regra visual: DEPUIS aponta pro passado do momento presente. PENDANT desenha uma linha inteira.",
      ],
      en: [
        "DEPUIS + PRESENT tense = still happening. « Je vis à Paris depuis 3 ans » (I still live there).",
        "PENDANT + any tense = closed duration. « J'ai vécu à Paris pendant 3 ans » (I lived there, not anymore).",
        "Visual: DEPUIS points from the past to now. PENDANT draws a whole line.",
      ],
      fr: [
        "DEPUIS + présent (action en cours).",
        "PENDANT + tout temps (durée fermée).",
      ],
    },
    faq: {
      pt: [
        { q: "« Faz 5 anos que estudo francês » — depuis ou pendant?", a: "DEPUIS: « J'étudie le français depuis 5 ans ». Ainda estuda AGORA. Se tivesse parado: « J'ai étudié le français pendant 5 ans »." },
        { q: "Existe alguma exceção?", a: "PENDANT ces 3 dernières années (« ao longo dos últimos 3 anos ») pode aparecer com presente pra enfatizar continuidade recente. Mas DEPUIS é o padrão." },
      ],
      en: [
        { q: "« I've been studying French for 5 years »?", a: "DEPUIS: « J'étudie le français depuis 5 ans ». Still studying NOW. If stopped: « J'ai étudié le français pendant 5 ans »." },
        { q: "Any exception?", a: "PENDANT ces 3 dernières années can appear with present for recent-continuity emphasis. But DEPUIS is the default." },
      ],
      fr: [
        { q: "« J'apprends depuis / pendant 5 ans » ?", a: "Depuis si tu apprends encore." },
        { q: "Exception ?", a: "« Pendant ces 3 dernières années » possible avec présent." },
      ],
    },
  },
  { slug: "venir-vs-aller", updated: "2026-02-24", a: "Venir", b: "Aller",
    intro: {
      pt: "VENIR = vir (aproximar-se do falante ou de um ponto de referência). ALLER = ir (afastar-se, deslocar-se pra outro lugar). Aparentemente simples, mas em francês o ponto de referência é sempre o INTERLOCUTOR, não o falante.",
      en: "VENIR = to come (approach the speaker or a reference point). ALLER = to go (move away, travel elsewhere). Simple in appearance, but in French the reference point is always the LISTENER, not the speaker.",
      fr: "VENIR : rapprochement. ALLER : déplacement. Le point de référence est l'interlocuteur.",
    },
    rules: {
      pt: [
        "Se te chamam pra ir pra CASA DO INTERLOCUTOR: use VENIR (« Je viens »), não ALLER. Diferente do português (« vou aí »).",
        "ALLER = destino em outro lugar. « Je vais à Paris ». « Il va au marché ».",
        "VENIR + de + infinitivo = passado recente (« venho de fazer » = acabei de fazer). « Je viens de manger » (« acabei de comer »).",
        "ALLER + infinitivo = futuro próximo. « Je vais manger » (« vou comer »).",
      ],
      en: [
        "If called to the LISTENER's place: use VENIR (« Je viens »), not ALLER. Different from English (« I'll go »).",
        "ALLER = destination elsewhere. « Je vais à Paris ».",
        "VENIR + de + infinitive = recent past. « Je viens de manger » (« I just ate »).",
        "ALLER + infinitive = near future. « Je vais manger » (« I'm going to eat »).",
      ],
      fr: [
        "Venir vers l'interlocuteur, aller ailleurs.",
        "Venir de + inf = passé récent.",
        "Aller + inf = futur proche.",
      ],
    },
    faq: {
      pt: [
        { q: "« Vou aí em 5 min » como se diz?", a: "« J'arrive dans 5 minutes » ou « Je viens dans 5 minutes ». NUNCA « je vais dans 5 minutes » pra ir na casa da pessoa — é o erro clássico do brasileiro. « Aller » exige que o destino NÃO seja onde o interlocutor está." },
        { q: "« Acabei de chegar » — venir ou aller?", a: "VENIR: « Je viens d'arriver ». Passado recente = venir de + infinitivo. Padrão de expressão essencial em francês oral." },
      ],
      en: [
        { q: "« I'm coming in 5 min »?", a: "« J'arrive dans 5 minutes » or « Je viens dans 5 minutes ». NEVER « je vais dans 5 minutes » to reach the listener — classic mistake." },
        { q: "« I just arrived » — venir or aller?", a: "VENIR: « Je viens d'arriver ». Recent past = venir de + infinitive. Essential oral pattern." },
      ],
      fr: [
        { q: "« Je viens dans 5 min » ou « je vais » ?", a: "Vers l'interlocuteur : venir." },
        { q: "« Je viens d'arriver » — sens ?", a: "Passé récent, « je viens de + infinitif »." },
      ],
    },
  },
  { slug: "jour-vs-journee", updated: "2026-02-19", a: "Jour", b: "Journée",
    intro: {
      pt: "JOUR (masculino) = « dia » como unidade de tempo — data, contagem, ponto no calendário. JOURNÉE (feminino) = « dia » como duração vivida — o que aconteceu, o clima geral, a experiência. É a mesma dupla que MATIN/MATINÉE, SOIR/SOIRÉE, AN/ANNÉE. Errar troca o sentido.",
      en: "JOUR (masc.) = « day » as a time unit — date, count, calendar point. JOURNÉE (fem.) = « day » as lived duration — what happened, the vibe. Same pair as MATIN/MATINÉE, SOIR/SOIRÉE, AN/ANNÉE. Getting it wrong changes the meaning.",
      fr: "JOUR : unité de temps. JOURNÉE : durée vécue.",
    },
    rules: {
      pt: [
        "Contando dias? Use JOUR. « Trois jours à Paris » (3 dias em Paris). « Le jour de mon anniversaire » (o dia do meu aniversário).",
        "Falando do dia como experiência? Use JOURNÉE. « Bonne journée ! » (bom dia — o dia inteiro pela frente). « J'ai passé une belle journée » (passei um dia lindo).",
        "Cumprimentando de manhã cedo? BONJOUR (o cumprimento fixo). Cumprimentando depois das 10h? Pode usar BONNE JOURNÉE ao se despedir.",
        "Regra prática : se cabe « toute la » antes (« toute la journée »), é JOURNÉE. Se cabe número ou artigo pontual (« un jour »), é JOUR.",
      ],
      en: [
        "Counting days? JOUR. « Trois jours à Paris ». « Le jour de mon anniversaire ».",
        "Talking about the day as an experience? JOURNÉE. « Bonne journée ! » (have a good day). « J'ai passé une belle journée ».",
        "Rule of thumb: if « toute la » fits (« all day long »), use JOURNÉE. If a count/article fits (« a day »), use JOUR.",
      ],
      fr: [
        "Jour = ponctuel. Journée = durée.",
        "« Toute la journée » : jamais « tout le jour ».",
        "« Bonjour » à l'arrivée, « bonne journée » au départ.",
      ],
    },
    faq: {
      pt: [
        { q: "Digo « bonjour » ou « bonne journée » ao chegar?", a: "BONJOUR. Sempre. É o cumprimento de abertura, obrigatório em qualquer interação (loja, elevador, colega). BONNE JOURNÉE é a despedida, deseja que o resto do dia seja bom." },
        { q: "Como se diz « passei um dia lindo »?", a: "« J'ai passé une belle journée ». Nunca « un beau jour » — isso significa « certo dia », no sentido narrativo. A duração vivida sempre pede JOURNÉE." },
        { q: "E « todo dia »?", a: "TOUS LES JOURS (unidade, contagem). « Je bois du café tous les jours ». Diferente de « toute la journée » (« o dia inteiro »)." },
        { q: "Serve pra matin/matinée e soir/soirée?", a: "Sim, mesmíssimo padrão. MATIN/SOIR = ponto no tempo (« ce matin »). MATINÉE/SOIRÉE = duração vivida (« bonne soirée », « toute la matinée »). Aprender jour/journée destrava os 3 pares." },
      ],
      en: [
        { q: "« Bonjour » or « bonne journée » on arrival?", a: "BONJOUR — always. It's the mandatory opening greeting. BONNE JOURNÉE is the farewell — wishing the rest of the day well." },
        { q: "How to say « I had a lovely day »?", a: "« J'ai passé une belle journée ». Never « un beau jour » — that means « one day » (narrative)." },
        { q: "Does it apply to matin/matinée and soir/soirée?", a: "Exact same pattern. Mastering jour/journée unlocks all three pairs." },
      ],
      fr: [
        { q: "« Bonjour » ou « bonne journée » ?", a: "Bonjour à l'arrivée, bonne journée au départ." },
        { q: "« Toute la journée » ou « tout le jour » ?", a: "Toujours « toute la journée »." },
      ],
    },
  },
];

// Route helpers
export const PROGRAMMATIC_ROUTES = [
  ...HOW_TO_SAY.map((e) => ({ path: `/frances/como-dizer/${e.slug}`, type: "how", entry: e })),
  ...PRONUNCIATION.map((e) => ({ path: `/frances/pronuncia/${e.slug}`, type: "pron", entry: e })),
  ...DIFFERENCES.map((e) => ({ path: `/frances/diferenca/${e.slug}`, type: "diff", entry: e })),
];

export function getEntry(type, slug) {
  if (type === "how") return HOW_TO_SAY.find((e) => e.slug === slug);
  if (type === "pron") return PRONUNCIATION.find((e) => e.slug === slug);
  if (type === "diff") return DIFFERENCES.find((e) => e.slug === slug);
  return null;
}
